import { useHomepageHydration } from "../../context/HomepageRenderContext";
import { lazyWithStyles } from "../../utils/lazyWithStyles";
import campingIcon from "../../assets/navigation/camping.webp";
import walkIcon from "../../assets/navigation/walk.webp";
import pinIcon from "../../assets/navigation/pin.webp";
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { differenceInCalendarDays, format, isValid, parseISO } from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import ContinueBookingBar from "./ContinueBookingBar";
import CartSummaryPill from "./CartSummaryPill";
import RewardCoinPill from "../Rewards/RewardCoinPill";
import PortalBrandLogo from "../Brand/PortalBrandLogo";
import GuestMixSelector from "../Booking/GuestMixSelector";
import { useLocationSearchIndex } from "../../hooks/useLocationSearchIndex";
import { useLocationSearchRemote } from "../../hooks/useLocationSearchRemote";
import { useLocationSearchDefaults } from "../../hooks/useLocationSearchDefaults";
import { filterLocationResults } from "../Booking/locationSearch";
import { fetchDestinationBySlug } from "../../api/destinationApi";
import { fetchDestinationCamps } from "../../api/exploreApi";
import { fetchSafariFeed } from "../../api/safariApi";
import { fetchCruiseFeed } from "../../api/cruiseApi";
import { fetchTrekFeed } from "../../api/trekApi";
import {
  extractExperienceCategoryRows,
  fetchExperienceCategories,
} from "../../api/experienceCategoryApi";
import { recordContinueBooking } from "../../utils/continueBookingStorage";
import { useWebsiteConfig } from "../../context/WebsiteConfigContext";
import { useTheme } from "../../context/ThemeContext";
import {
  BRAND_LABELS,
  BRAND_LABELS_LOWER,
} from "../../utils/brandLabels";
import { isPortalTestEnvironment } from "../../utils/environmentMode";
import {
  canonicalizeDistrictName,
  canonicalizeDistrictSlug,
} from "../../utils/odishaDistrictNames";

import {
  selectLandingSearch,
  clearSearchState,
  clearUserBookings,
  setSearchDates,
  setSearchExperienceType,
  setSearchExperienceCategory,
  setSearchGuests,
  setSearchLocation,
  setSearchMode,
  setSearchState,
} from "../../store";
import { slugify } from "../../utils/slugify";
import { normalizeCampSlug, resolveCampSlugFromEntry } from "../../utils/campSlug";
import { buildHtmlPreviewText } from "../../utils/htmlPreview";
import { AUTH_EVENT, clearSession, getActiveSession } from "../../utils/nightStayAccount";
import { clearTokens } from "../../utils/authTokens";
import {
  normalizeHomeLandingMode,
  resolveHomeLandingModeFromPath,
  resolveHomeLandingRoute,
} from "../../utils/homeLandingRoutes";
import "./HeaderThree.css";
import { observeHeaderHeight } from "./headerMetrics";

const ExperienceCartConfigurator = lazyWithStyles(() =>
  import("../../Pages/Explore/ExperienceCartConfigurator"),
);
const SideMenu = lazyWithStyles(() => import("./SideMenu"));
const MobileMenu = lazyWithStyles(() => import("./MobileMenu"));
const LocationSearchCombobox = lazyWithStyles(() => import("../Booking/LocationSearchCombobox"));
const MobileWhereSheet = lazyWithStyles(() => import("../Booking/MobileWhereSheet"));
const DateRangeInput = lazyWithStyles(() => import("../../Pages/Explore/Camp/DateRangeInput"));
const StaySequentialDateFields = lazyWithStyles(() => import("../Booking/StaySequentialDateFields"));

const STICKY_SCROLL_OFFSET = 140;
const HOME_PROMOTION_BANNER_ID = "home-promotion-banner";
const EXPERIENCE_SECTION_ID = "overview-section-experience-inventory";
const STAY_SECTION_ID = "overview-section-where-to-stay";
const LANDING_SEARCH_PERSIST_KEY = "ecotour.landingSearch.v1";
const DEFAULT_GUESTS = { adults: 2, children: 0, infants: 0 };
const SEARCH_WIZARD_BREAKPOINT = 1100;

function DeferredControlFallback({ label, className = "" }) {
  return (
    <div
      className={`flex min-h-20 items-center justify-center px-4 py-6 text-sm font-semibold text-slate-500 ${className}`}
      role="status"
      aria-live="polite"
    >
      {label}
    </div>
  );
}

const UI = {
  // wrappers
  menuAreaBase: "ecotour-site-header__surface w-full border-b border-transparent transition-all duration-300 ease-soft",
  landingWrap: "px-1.5 sm:px-2 lg:px-3 xl:px-4",
  container: "relative mx-auto w-full max-w-[min(1440px,94vw)]",

  // pills/buttons
  pill:
    "ecotour-header-control inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white text-[12px] font-semibold text-slate-900 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
  iconPillBlue:
    "ecotour-header-control__icon inline-flex h-6 w-6 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700",
  iconPillSoft:
    "ecotour-header-control__icon inline-flex h-6 w-6 items-center justify-center rounded-xl bg-slate-50 text-slate-700",
  iconBtn:
    "ecotour-header-icon-button inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-emerald-800 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 md:h-8 md:w-8",
  tinyX:
    "ecotour-booking-clear inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-emerald-50 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
  modeToggleWrap:
    "inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-0.5 shadow-sm",
  modeToggleBtn:
    "inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
  modeInlineWrap:
    "relative inline-flex items-center",
  modeInlineBtn:
    "inline-flex w-[140px] items-center justify-between gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
  modeInlineMenu:
    "absolute left-0 top-full z-20 mt-2 w-40 rounded-2xl border border-slate-200 bg-white p-1 shadow-[0_16px_40px_rgba(15,23,42,0.16)]",
  modeInlineItem:
    "flex w-full items-center gap-2 rounded-xl px-3 py-2 text-[12px] font-semibold text-slate-700 transition hover:bg-emerald-50",

  // search bar
  bar:
    "ecotour-booking-bar flex w-full flex-wrap items-stretch gap-2.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-[0_8px_24px_rgba(15,23,42,0.10)] sm:px-3.5 sm:py-2 md:flex-nowrap focus-within:border-emerald-200 focus-within:ring-2 focus-within:ring-emerald-500/30",
  fieldBtn: "ecotour-booking-field flex min-w-0 flex-1 flex-col items-start justify-center text-left focus-visible:outline-none",
  fieldBtnCompact: "ecotour-booking-field flex min-w-0 flex-1 items-center justify-center gap-1 py-0.5 text-left focus-visible:outline-none sm:gap-1.5 sm:py-1",
  fieldLabel: "ecotour-booking-field__label text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500",
  fieldValue: "ecotour-booking-field__value truncate text-[14px] font-semibold text-slate-900",
  divider: "ecotour-booking-divider hidden h-7 w-px bg-slate-200 md:block",
  fieldLabelCompact: "ecotour-booking-field__label text-[8px] font-semibold uppercase tracking-[0.16em] text-slate-400 sm:text-[9px] sm:tracking-[0.18em]",
  fieldValueCompact: "ecotour-booking-field__value truncate text-[11px] font-semibold text-slate-900 sm:text-[12px]",
  dividerCompact: "ecotour-booking-divider hidden h-6 w-px bg-slate-200/80 md:block",
  fieldIconBase:
    "ecotour-booking-field__icon inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] shadow-sm",
  fieldIconBaseCompact:
    "ecotour-booking-field__icon inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] shadow-sm sm:h-4 sm:w-4 sm:text-[9px]",
  fieldIconWhat: "bg-emerald-100 text-emerald-700",
  fieldIconWhere: "bg-emerald-100 text-emerald-700",
  fieldIconWhen: "bg-amber-100 text-amber-700",
  fieldIconWho: "bg-indigo-100 text-indigo-700",
  fieldIconFrom: "bg-cyan-100 text-cyan-700",
  fieldIconTo: "bg-violet-100 text-violet-700",
  searchBtn:
    "ecotour-booking-search-button inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-700 text-white shadow-sm transition hover:-translate-y-[1px] hover:bg-emerald-800 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
  searchBtnCompact:
    "ecotour-booking-search-button inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-800 text-white shadow-[0_6px_16px_rgba(15,23,42,0.18)] transition hover:-translate-y-[1px] hover:bg-emerald-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 sm:h-8 sm:w-8",

  // popovers
  popover:
    "ecotour-booking-popover absolute left-0 right-0 top-[calc(100%+10px)] z-[1300] overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.16)]",
  popoverCenter:
    "ecotour-booking-popover absolute left-1/2 top-[calc(100%+10px)] z-[1250] w-[min(95vw,720px)] -translate-x-1/2 overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.16)]",
  barCompact:
    "ecotour-booking-bar flex w-full items-center gap-1 rounded-full border border-slate-200/80 bg-white/90 px-1.5 py-0.5 shadow-[0_6px_16px_rgba(15,23,42,0.08)] backdrop-blur-md sm:gap-1.5 sm:px-2 sm:py-1 focus-within:border-emerald-200 focus-within:ring-2 focus-within:ring-emerald-500/30",
  menuPopover:
    "absolute right-0 top-full z-[1200] mt-2 rounded-2xl border border-slate-100 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]",
  menuPanel:
    "rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_24px_60px_rgba(15,23,42,0.12)]",
  menuItem:
    "flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-[13px] font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-900",
  menuItemActive: "bg-slate-900 text-white hover:bg-slate-900 hover:text-white",
  menuSubtle: "text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400",
  menuDivider: "my-2 h-px w-full bg-slate-200/70",

};

const LANDING_TABS = [
  {
    key: "stays",
    label: "Stays",
    iconSrc: campingIcon,
    iconStillScale: 0.74,
  },
  {
    key: "experiences",
    label: "Experiences",
    iconSrc: walkIcon,
    iconStillScale: 0.72,
  },
  {
    key: "explore",
    label: "Explore",
    iconSrc: pinIcon,
    iconStillScale: 0.7,
    badge: "New",
  },
];
const MENU_PRIMARY_ITEMS = [
  { key: "destinations", label: BRAND_LABELS.destinations, icon: "fa-solid fa-tree" },
  { key: "stay-options", label: BRAND_LABELS.stays, icon: "fa-solid fa-bed" },
  { key: "experiences", label: BRAND_LABELS.experiences, icon: "fa-solid fa-compass" },
  { key: "packages", label: "Packages", icon: "fa-solid fa-suitcase" },
  { key: "districts", label: BRAND_LABELS.districts, icon: "fa-solid fa-location-dot" },
];

const ACCOUNT_QUICK_LINKS = [
  { label: "Bookings", path: "/account/bookings", icon: "fa-regular fa-calendar-check" },
  { label: "Wishlist", path: "/account/wishlist", icon: "fa-regular fa-heart" },
  { label: "Profile", path: "/account/profile", icon: "fa-regular fa-user" },
  { label: "Support", path: "/support/queries", icon: "fa-regular fa-comment-dots" },
];

const PORTAL_INFO_LINKS = [
  { label: "Tour Operators", path: "/tour-operators", icon: "fa-solid fa-address-book" },
];

const EXPERIENCE_LINKS = {
  safari: "/experiences",
  cruise: "/cruise-boating",
  trek: "/treks-trails",
};

const EXPERIENCE_TYPES = [
  {
    key: "safari",
    label: "Jungle safari",
    description: "Wildlife drives & entry gates",
    icon: "fa-solid fa-binoculars",
  },
  {
    key: "cruise",
    label: BRAND_LABELS.cruise,
    description: "Lakes, rivers & coastal rides",
    icon: "fa-solid fa-sailboat",
  },
  {
    key: "trek",
    label: "Treks & trails",
    description: "Guided treks & forest walks",
    icon: "fa-solid fa-person-hiking",
  },
];

const TREK_CATEGORY = {
  key: "trek",
  slug: "trek",
  label: "Treks & trails",
  description: "Guided treks & forest walks",
  icon: "fa-solid fa-person-hiking",
  type: "pseudo",
};

const formatDateRangeLabel = (start, end, single = false) => {
  if (single) {
    if (isValid(start)) return format(start, "dd MMM");
    return "Planned date";
  }
  if (isValid(start) && isValid(end)) return `${format(start, "dd MMM")} – ${format(end, "dd MMM")}`;
  if (isValid(start)) return format(start, "dd MMM");
  return "Planned Dates";
};

const formatDateRangeTopLabel = (start, end, single = false) => {
  if (single) {
    if (isValid(start)) return format(start, "dd MMM yyyy");
    return "Select date";
  }
  if (isValid(start) && isValid(end)) {
    const sameYear = start.getFullYear() === end.getFullYear();
    if (sameYear) {
      return `${format(start, "dd MMM")} – ${format(end, "dd MMM yyyy")}`;
    }
    return `${format(start, "dd MMM yyyy")} – ${format(end, "dd MMM yyyy")}`;
  }
  if (isValid(start)) return format(start, "dd MMM yyyy");
  return "Select dates";
};

const formatDateParam = (value) => {
  if (!value || !(value instanceof Date)) return "";
  return format(value, "yyyy-MM-dd");
};

export const PortalTestNavBadge = () => (
  <div
    className="portal-test-nav-badge hidden max-w-[128px] flex-col rounded-2xl border border-red-200 bg-red-50/95 px-3 py-1 text-left text-[10px] font-extrabold uppercase tracking-[0.18em] text-red-700 shadow-sm backdrop-blur-sm min-[480px]:inline-flex"
    aria-label="This is a test site"
  >
    <span>Test Site</span>
  </div>
);

export function ThemeToggle({ className = "" }) {
  const { theme, toggleTheme } = useTheme();
  const hydrating = useHomepageHydration();
  const isDark = !hydrating && theme === "dark";
  const label = isDark ? "Switch to light mode" : "Switch to dark mode";

  return (
    <button
      type="button"
      className={`${UI.iconBtn} ecotour-theme-toggle ${className}`.trim()}
      onClick={toggleTheme}
      aria-label={label}
      title={label}
    >
      {isDark ? (
        <svg
          className="ecotour-theme-toggle__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          aria-hidden="true"
          data-theme-icon="sun"
        >
          <circle cx="12" cy="12" r="3.5" />
          <path d="M12 2.5v2M12 19.5v2M4.5 12h-2M21.5 12h-2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4" />
        </svg>
      ) : (
        <svg
          className="ecotour-theme-toggle__icon"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          data-theme-icon="moon"
        >
          <path d="M20.3 15.4A8.4 8.4 0 0 1 8.6 3.7 8.6 8.6 0 1 0 20.3 15.4Z" />
        </svg>
      )}
    </button>
  );
}

const parseDateParam = (value) => {
  if (!value) return null;
  const parsed = parseISO(String(value));
  return isValid(parsed) ? parsed : null;
};

const normalizeSearchMode = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "experiences" || raw === "experience") return "experiences";
  if (raw === "itinerary" || raw === "itinerary-planner") return "itinerary";
  return "stays";
};

const isBookingLandingMode = (value) => value === "stays" || value === "experiences";

const normalizeExperienceType = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "safari" || raw === "jungle-safari") return "safari";
  if (raw === "cruise" || raw === "cruise-boating" || raw === "boating") return "cruise";
  if (raw === "trek" || raw === "trekking" || raw === "trail") return "trek";
  return "safari";
};

const inferExperienceTypeFromCategory = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "safari";
  if (raw.includes("trek") || raw.includes("trail")) return "trek";
  if (raw.includes("cruise") || raw.includes("boat")) return "cruise";
  if (raw.includes("safari")) return "safari";
  return normalizeExperienceType(raw);
};

const inferExperienceTypeFromTemplate = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "cruise") return "cruise";
  if (raw === "trail" || raw === "trek") return "trek";
  if (raw === "safari") return "safari";
  return "";
};

const normalizeExperienceCategory = (value) => String(value || "").trim().toLowerCase();
const humanizeDestinationSlug = (value = "") =>
  String(value || "")
    .trim()
    .replace(/-destination$/i, "")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
const normalizeSelectionType = (selection = {}) => {
  const explicit = String(selection?.type || "").trim().toLowerCase();
  if (explicit) return explicit;
  if (selection?.campSlug || (selection?.destinationSlug && selection?.location)) return "camp";
  if (selection?.destinationSlug) return "destination";
  if (selection?.district) return "district";
  return "";
};
const normalizeExperienceCategoryKey = (value) => {
  const raw = normalizeExperienceCategory(value);
  if (!raw) return "";
  if (
    raw === "trek" ||
    raw === "trekking" ||
    raw === "trail" ||
    raw === "trails" ||
    raw.includes("trek") ||
    raw.includes("trail")
  ) {
    return TREK_CATEGORY.slug;
  }
  return raw;
};

const normalizeGuestMix = (input) => {
  const adult = Math.max(1, Number(input?.adults ?? input?.adult ?? input?.adultCount ?? 1) || 1);
  const child = Math.max(0, Number(input?.children ?? input?.child ?? input?.childCount ?? 0) || 0);
  const infant = Math.max(0, Number(input?.infants ?? input?.infant ?? input?.infantCount ?? 0) || 0);
  return { adults: adult, children: child, infants: infant };
};

function FieldLabel({ icon, toneClass, label, textClass }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`${UI.fieldIconBase} ${toneClass}`}>
        <i className={icon} aria-hidden="true" />
      </span>
      <span className={textClass}>{label}</span>
    </span>
  );
}

export function LandingModeNav({ value, onChange, className = "", compact = false }) {
  const moveFocus = (event, currentIndex) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = currentIndex;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = LANDING_TABS.length - 1;
    if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + LANDING_TABS.length) % LANDING_TABS.length;
    }
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % LANDING_TABS.length;
    const nextTab = LANDING_TABS[nextIndex];
    onChange?.(nextTab.key);
    event.currentTarget.parentElement?.querySelectorAll('[role="tab"]')?.[nextIndex]?.focus();
  };

  return (
    <div
      className={`ecotour-mode-nav ${compact ? "ecotour-mode-nav--compact" : ""} ${className}`}
      role="tablist"
      aria-label="Travel modes"
    >
      {LANDING_TABS.map((tab, tabIndex) => {
        const isActive = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-current={isActive ? "page" : undefined}
            aria-controls="home-landing-content"
            tabIndex={isActive ? 0 : -1}
            className="ecotour-mode-nav__item"
            style={{
              "--ecotour-tab-index": tabIndex,
            }}
            onClick={() => onChange?.(tab.key)}
            onKeyDown={(event) => moveFocus(event, tabIndex)}
          >
            <span className="ecotour-mode-nav__icon-wrap" aria-hidden="true">
              <img
                src={tab.iconSrc}
                alt=""
                width="52"
                height="52"
                className="ecotour-mode-nav__icon ecotour-mode-nav__icon--still"
                style={{
                  "--ecotour-icon-still-scale": tab.iconStillScale,
                }}
                loading="eager"
                decoding="async"
                onError={(event) => {
                  event.currentTarget.src = tab.iconSrc;
                }}
              />
              {tab.badge ? <span className="ecotour-mode-nav__badge">{tab.badge}</span> : null}
            </span>
            <span className="ecotour-mode-nav__label">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function BookingBar({
  variant = "landing", // "landing" | "compact"
  show,
  mode = "stays",
  showModeToggle = false,
  mobileFriendlyCompact = false,
  experienceCategory = null,
  experienceSummary,
  locationSummary,
  dateSummary,
  hasDateSelection = false,
  searchButtonLabel,
  hasLocationSelection,
  guestMix,
  maxGuests = 16,
  originInput = "",
  destinationInput = "",
  onOriginInputChange,
  onDestinationInputChange,
  // popover state/refs
  isExperienceOpen,
  isLocationOpen,
  isDateOpen,
  experienceTriggerRef,
  locationTriggerRef,
  dateTriggerRef,
  locationPopoverRef,
  datePopoverRef,
  experiencePopoverId,
  locationPopoverId,
  datePopoverId,
  // handlers
  onModeChange,
  onToggleExperience,
  onToggleLocation,
  onToggleDate,
  onClearLocation,
  onGuestChange,
  onToggleGuests,
  onSearch,
  // popover content
  experiencePopover,
  locationPopover,
  datePopover,
}) {
  const isCompact = variant === "compact";
  const isItinerary = mode === "itinerary";
  const isExperience = mode === "experiences";
  const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
  const modeMenuRef = useRef(null);
  const fieldBtnClass = isCompact ? UI.fieldBtnCompact : `${UI.fieldBtn} gap-1.5 py-1.5`;
  const fieldLabelClass = isCompact ? UI.fieldLabelCompact : UI.fieldLabel;
  const fieldValueClass = isCompact ? UI.fieldValueCompact : UI.fieldValue;
  const fieldIconClass = isCompact ? UI.fieldIconBaseCompact : UI.fieldIconBase;
  const dividerClass = isCompact ? UI.dividerCompact : UI.divider;
  const experienceConfig =
    experienceCategory || { label: "Category", icon: "fa-solid fa-compass" };
  const showWhatLabel = !experienceSummary || experienceSummary === "Category";
  const showWhereLabel = !hasLocationSelection;
  const showWhenLabel = !hasDateSelection;

  useEffect(() => {
    if (!show || !isModeMenuOpen) return;
    const handleClick = (event) => {
      if (!modeMenuRef.current?.contains(event.target)) {
        setIsModeMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsModeMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModeMenuOpen]);

  if (!show) return null;

  const renderValueWithIcon = (icon, toneClass, value) => (
    <span className={`inline-flex items-center gap-2 ${fieldValueClass}`}>
      <span className={`${fieldIconClass} ${toneClass}`}>
        <i className={icon} aria-hidden="true" />
      </span>
      {value}
    </span>
  );

  return (
    <div
      className={
        isCompact
          ? "ecotour-booking-bar-shell ecotour-booking-bar-shell--compact flex flex-1 justify-center"
          : "ecotour-booking-bar-shell ecotour-booking-bar-shell--landing flex justify-center px-1.5 pb-1.5 md:px-2"
      }
    >
      <div className={isCompact ? "relative w-full max-w-2xl" : "relative w-full max-w-[min(920px,94vw)]"}>
        <div className={isCompact ? `${UI.barCompact} ${mobileFriendlyCompact ? "flex-nowrap" : ""}` : UI.bar}>
          {isItinerary ? (
            <>
              <div className="flex min-w-[200px] flex-1 flex-col gap-0.5">
                <FieldLabel
                  icon="fa-solid fa-location-crosshairs"
                  toneClass={UI.fieldIconFrom}
                  label="From"
                  textClass={fieldLabelClass}
                />
                <input
                  type="text"
                  value={originInput}
                  onChange={(event) => onOriginInputChange?.(event.target.value)}
                  placeholder="Starting point"
                  className="w-full bg-transparent text-sm font-semibold text-slate-900 placeholder:text-slate-400 focus:outline-none"
                />
              </div>

              <span className={UI.divider} aria-hidden="true" />

              <div className="flex min-w-[200px] flex-1 flex-col gap-0.5">
                <FieldLabel
                  icon="fa-solid fa-flag-checkered"
                  toneClass={UI.fieldIconTo}
                  label="To"
                  textClass={fieldLabelClass}
                />
                <input
                  type="text"
                  value={destinationInput}
                  onChange={(event) => onDestinationInputChange?.(event.target.value)}
                  placeholder="Final stop"
                  className="w-full bg-transparent text-sm font-semibold text-slate-900 placeholder:text-slate-400 focus:outline-none"
                />
              </div>
            </>
          ) : (
            <>
              {showModeToggle ? (
                <>
                  <div className={UI.modeInlineWrap} ref={modeMenuRef}>
                    {(() => {
                      const options = [
                        { key: "stays", label: "Stays", icon: "fa-solid fa-bed" },
                        { key: "experiences", label: "Experiences", icon: "fa-solid fa-compass" },
                      ];
                      const current = options.find((item) => item.key === mode) || options[0];
                      return (
                        <>
                          <button
                            type="button"
                            className={UI.modeInlineBtn}
                            aria-haspopup="menu"
                            aria-expanded={isModeMenuOpen}
                            onClick={() => setIsModeMenuOpen((prev) => !prev)}
                          >
                            <i className={current.icon} aria-hidden="true" />
                            <span>{current.label}</span>
                            <i className="fa-solid fa-chevron-down text-[9px] text-slate-400" aria-hidden="true" />
                          </button>
                          {isModeMenuOpen ? (
                            <div className={UI.modeInlineMenu} role="menu" aria-label="Booking mode">
                              {options.map((item) => {
                                const isActive = mode === item.key;
                                return (
                                  <button
                                    key={item.key}
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={isActive}
                                    onClick={() => {
                                      onModeChange?.(item.key);
                                      setIsModeMenuOpen(false);
                                    }}
                                    className={`${UI.modeInlineItem} ${isActive ? "bg-emerald-800 text-white" : ""}`}
                                  >
                                    <i className={item.icon} aria-hidden="true" />
                                    <span>{item.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                  <span className={dividerClass} aria-hidden="true" />
                </>
              ) : null}

              {/* WHAT */}
              {isExperience ? (
                <>
                  <button
                    type="button"
                    ref={experienceTriggerRef}
                    onClick={onToggleExperience}
                    className={`${fieldBtnClass} ${isCompact ? "min-w-[120px]" : "min-w-[140px]"}`}
                    aria-expanded={isExperienceOpen}
                    aria-haspopup="dialog"
                    aria-controls={experiencePopoverId || undefined}
                  >
                  {renderValueWithIcon(
                    experienceConfig.icon,
                    UI.fieldIconWhat,
                    showWhatLabel ? "Category" : experienceSummary
                  )}
                </button>
                  <span className={dividerClass} aria-hidden="true" />
                </>
              ) : null}

              {/* WHERE */}
              <div
                className={`flex ${
                  isCompact ? (mobileFriendlyCompact ? "min-w-0" : "min-w-[140px]") : "min-w-[170px]"
                } flex-1 items-center gap-1`}
              >
                <button
                  type="button"
                  ref={locationTriggerRef}
                  onClick={onToggleLocation}
                  className={fieldBtnClass}
                  aria-expanded={isLocationOpen}
                  aria-haspopup="dialog"
                  aria-controls={locationPopoverId || undefined}
                >
                  {renderValueWithIcon(
                    "fa-solid fa-location-dot",
                    UI.fieldIconWhere,
                    showWhereLabel ? "Where" : locationSummary
                  )}
                </button>

                {hasLocationSelection ? (
                  <button
                    type="button"
                    onClick={onClearLocation}
                    className={UI.tinyX}
                    aria-label="Clear selection"
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                ) : null}
              </div>

              <span className={dividerClass} aria-hidden="true" />

              {/* WHEN */}
              <button
                type="button"
                ref={dateTriggerRef}
                onClick={onToggleDate}
                className={`${fieldBtnClass} ${
                  isCompact
                    ? mobileFriendlyCompact
                      ? "min-w-0"
                      : "hidden sm:flex min-w-[120px]"
                    : "min-w-[150px]"
                }`}
                aria-expanded={isDateOpen}
                aria-haspopup="dialog"
                aria-controls={datePopoverId || undefined}
              >
                {renderValueWithIcon(
                  "fa-regular fa-calendar",
                  UI.fieldIconWhen,
                  showWhenLabel ? "When" : dateSummary
                )}
              </button>

              {!isExperience ? (
                <>
                  <span className={`${dividerClass} ${isCompact ? "hidden lg:block" : ""}`} aria-hidden="true" />

                  {/* WHO */}
                  <div
                    className={`${
                      isCompact ? (mobileFriendlyCompact ? "flex" : "hidden lg:flex") : "flex"
                    } ${isCompact && mobileFriendlyCompact ? "min-w-0" : "min-w-[160px]"} flex-1 items-center`}
                    onMouseDown={() => {
                      // close popovers when interacting with GuestMix
                      if (isExperienceOpen) onToggleExperience?.(true);
                      if (isLocationOpen) onToggleLocation?.(true);
                      if (isDateOpen) onToggleDate?.(true);
                    }}
                  >
                    <GuestMixSelector
                      variant="inline"
                      value={guestMix}
                      onChange={onGuestChange}
                      onOpenRequest={onToggleGuests}
                      maxGuests={maxGuests}
                      prefixIcon={
                        <span className={`${fieldIconClass} ${UI.fieldIconWho}`}>
                          <i className="fa-solid fa-user-group" aria-hidden="true" />
                        </span>
                      }
                    />
                  </div>
                </>
              ) : null}
            </>
          )}

          {/* SEARCH */}
          <button
            type="button"
            className={isCompact ? UI.searchBtnCompact : UI.searchBtn}
            aria-label={searchButtonLabel}
            onClick={onSearch}
          >
            <i
              className={`fa-solid fa-magnifying-glass ${isCompact ? "text-[10px] sm:text-[12px]" : "text-[13px]"}`}
              aria-hidden="true"
            />
          </button>
        </div>

        {/* POPOVERS */}
        {!isItinerary ? experiencePopover : null}
        {!isItinerary ? locationPopover : null}
        {!isItinerary ? datePopover : null}
      </div>
    </div>
  );
}

function HeaderThree({
  isStickyDisabled = false,
  initialAppearance = "solid",
  enableLandingNav = false,
  landingMode = "",
  syncLandingSearchMode = true,
}) {
  const hydrating = useHomepageHydration();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hostedForms, contentNavigation } = useWebsiteConfig();
  const experiencePopoverIdLanding = useId();
  const locationPopoverIdLanding = useId();
  const datePopoverIdLanding = useId();
  const experiencePopoverIdCompact = useId();
  const locationPopoverIdCompact = useId();
  const datePopoverIdCompact = useId();
  const wizardTitleId = useId();
  const wizardDescId = useId();

  const landingSearch = useSelector(selectLandingSearch);
  const routeLandingMode =
    normalizeHomeLandingMode(landingMode) ||
    resolveHomeLandingModeFromPath(location.pathname);
  const routeSearchMode = isBookingLandingMode(routeLandingMode) ? routeLandingMode : "";
  const searchMode = routeSearchMode || landingSearch?.mode || "stays";
  const activeLandingMode = routeLandingMode || searchMode;
  const showTestPortalBadge = isPortalTestEnvironment();
  const experienceType = normalizeExperienceType(landingSearch?.experienceType);
  const experienceCategory = normalizeExperienceCategory(landingSearch?.experienceCategory);

  const [isSticky, setIsSticky] = useState(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [pendingAuthRequest, setPendingAuthRequest] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileMenuSection, setMobileMenuSection] = useState("");
  const [isCartConfiguratorOpen, setIsCartConfiguratorOpen] = useState(false);
  const [cartInitialStep, setCartInitialStep] = useState(1);
  const [cartResumeTarget, setCartResumeTarget] = useState(null);
  const [menuPrimary, setMenuPrimary] = useState("destinations");
  const [menuDestinationSlug, setMenuDestinationSlug] = useState("");
  const [menuExperienceType, setMenuExperienceType] = useState("safari");
  const [storedSession, setSession] = useState(() => getActiveSession());
  const session = hydrating ? null : storedSession;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  const [isSearchViewportCompact, setIsSearchViewportCompact] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < SEARCH_WIZARD_BREAKPOINT;
  });
  const [isSearchWizardOpen, setIsSearchWizardOpen] = useState(false);
  const [mobileSheet, setMobileSheet] = useState("main"); // main | category | location | date | guests
  const [mobileDraftDates, setMobileDraftDates] = useState(null);
  const [campInventoryState, setCampInventoryState] = useState({});
  const [menuDestinationDetailState, setMenuDestinationDetailState] = useState({});
  const [menuActivityCountState, setMenuActivityCountState] = useState({});
  const [experienceCategoryMaster, setExperienceCategoryMaster] = useState([]);
  const [isExperienceOpen, setIsExperienceOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");
  const [wizardLocationQuery, setWizardLocationQuery] = useState("");
  const [isWizardSearching, setIsWizardSearching] = useState(false);
  const [locationSelection, setLocationSelection] = useState(() => ({
    district: landingSearch?.location?.district || "",
    destinationSlug: landingSearch?.location?.destinationSlug || "",
    destinationLabel: landingSearch?.location?.destinationLabel || "",
    location: landingSearch?.location?.location || "",
    campSlug: landingSearch?.location?.campSlug || "",
    experienceType: landingSearch?.location?.experienceType || "",
    experienceSlug: landingSearch?.location?.experienceSlug || "",
    experiencePath: landingSearch?.location?.experiencePath || "",
    experienceCategory: landingSearch?.location?.experienceCategory || "",
  }));
  const [itineraryInputs, setItineraryInputs] = useState(() => ({
    origin: landingSearch?.itinerary?.origin || "",
    destination: landingSearch?.itinerary?.destination || "",
  }));

  const initialDates = useMemo(
    () => ({
      startDate: parseDateParam(landingSearch?.dates?.startDate) || null,
      endDate: parseDateParam(landingSearch?.dates?.endDate) || null,
    }),
    [landingSearch?.dates?.endDate, landingSearch?.dates?.startDate],
  );

  const [dateRange, setDateRange] = useState(() => initialDates);
  const [guestMix, setGuestMix] = useState(() => normalizeGuestMix(landingSearch?.guests));
  const activeHostedForms = useMemo(
    () =>
      (Array.isArray(hostedForms) ? hostedForms : []).map((form) => ({
        ...form,
        summaryPreview: buildHtmlPreviewText(form?.summary || "", 120),
      })),
    [hostedForms],
  );
  const activeContentLinks = useMemo(
    () =>
      (Array.isArray(contentNavigation?.primaryLinks) ? contentNavigation.primaryLinks : []).map((item) => ({
        ...item,
        summaryPreview: buildHtmlPreviewText(item?.description || "", 120),
      })),
    [contentNavigation],
  );
  const contentMenuLabel = activeContentLinks.length === 1
    ? activeContentLinks[0]?.label || "Resources"
    : "Resources";
  const menuPrimaryItems = useMemo(
    () => [
      ...MENU_PRIMARY_ITEMS,
      ...(activeContentLinks.length
        ? [{ key: "content", label: contentMenuLabel, icon: "fa-regular fa-circle-question" }]
        : []),
      ...(activeHostedForms.length
        ? [{ key: "forms", label: "Forms", icon: "fa-regular fa-file-lines" }]
        : []),
    ],
    [activeContentLinks.length, activeHostedForms.length, contentMenuLabel],
  );

  const menuRef = useRef(null);
  const stickyWrapperRef = useRef(null);
  const menuButtonRef = useRef(null);
  const experienceTriggerRef = useRef(null);
  const experiencePopoverRef = useRef(null);
  const locationTriggerRef = useRef(null);
  const locationPopoverRef = useRef(null);
  const dateTriggerRef = useRef(null);
  const datePopoverRef = useRef(null);
  const lastSearchRef = useRef("");
  const wizardRef = useRef(null);
  const wizardCompletedRef = useRef(false);
  const lastWizardOpenRef = useRef(false);
  const searchPersistedRef = useRef(false);
  const lastNoResultsRef = useRef({ query: "", mode: "" });
  const authRequestSequenceRef = useRef(0);

  useEffect(() => {
    const shouldLoad =
      isExperienceOpen ||
      (isSearchWizardOpen && mobileSheet === "category");
    if (!shouldLoad) return undefined;
    let cancelled = false;
    fetchExperienceCategories()
      .then((response) => {
        if (cancelled) return;
        const rows = extractExperienceCategoryRows(response);
        setExperienceCategoryMaster(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setExperienceCategoryMaster([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isExperienceOpen, isSearchWizardOpen, mobileSheet]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia(`(max-width: ${SEARCH_WIZARD_BREAKPOINT - 1}px)`);
    const update = () => setIsSearchViewportCompact(media.matches);
    media.addEventListener?.("change", update);
    if (!media.addEventListener) media.addListener?.(update);
    return () => {
      media.removeEventListener?.("change", update);
      if (!media.removeEventListener) media.removeListener?.(update);
    };
  }, []);

  const trackEvent = useCallback((eventName, detail = {}) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("ecotour:telemetry", { detail: { event: eventName, ...detail } }));
  }, []);

  useLayoutEffect(() => {
    if (typeof document === "undefined" || !stickyWrapperRef.current) return undefined;
    return observeHeaderHeight(stickyWrapperRef.current);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleOpenCart = (event) => {
      const nextStep = Number(event?.detail?.step) || 1;
      setCartResumeTarget(event?.detail?.resumeTarget || null);
      setCartInitialStep(nextStep);
      setIsCartConfiguratorOpen(true);
    };
    window.addEventListener("ecotour:open-cart", handleOpenCart);
    return () => window.removeEventListener("ecotour:open-cart", handleOpenCart);
  }, []);

  useEffect(() => {
    const resumeTarget = location.state?.continueBookingResume || null;
    if (!resumeTarget) return;
    setCartResumeTarget(resumeTarget);
    setCartInitialStep(1);
    setIsCartConfiguratorOpen(true);
  }, [location.state]);

  const clearContinueBookingResumeIntent = useCallback(() => {
    setCartResumeTarget(null);
    if (!location.state?.continueBookingResume) return;
    const nextState = { ...(location.state || {}) };
    delete nextState.continueBookingResume;
    navigate(`${location.pathname}${location.search || ""}`, {
      replace: true,
      state: Object.keys(nextState).length ? nextState : null,
    });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {

    if (typeof window === "undefined") return;
    const handler = (event) => {
      const payload = event?.detail || null;
      if (!payload) return;
      recordContinueBooking(payload);
    };
    window.addEventListener("ecotour:continue-booking", handler);
    return () => window.removeEventListener("ecotour:continue-booking", handler);
  }, []);

  const handleEditItem = useCallback((item) => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ecotour:edit-room", { detail: { item } }));
    }
    setIsCartConfiguratorOpen(false);
    setCartInitialStep(1);
  }, []);

  useEffect(() => {
    if (!isSearchViewportCompact) setIsSearchWizardOpen(false);
  }, [isSearchViewportCompact]);

  const isStayMode = searchMode === "stays";
  const hasDateSelection = isStayMode
    ? Boolean(dateRange.startDate && dateRange.endDate)
    : Boolean(dateRange.startDate);

  const overlayEnabled = initialAppearance === "overlay";
  const isOverlayActive = overlayEnabled && !isSticky;
  const allowSticky = !isStickyDisabled || isMobile;

  const bookingIndexMode = searchMode === "experiences" ? "experiences" : "stays";
  const menuSearchEnabled = isMenuOpen || isMobileMenuOpen;
  const bookingSearchEnabled =
    isLocationOpen || (isSearchWizardOpen && mobileSheet === "location");
  const { index: menuSearchIndex } = useLocationSearchIndex({ enabled: menuSearchEnabled });
  const {
    index: bookingSearchIndex,
    error: bookingSearchError,
    status: bookingSearchStatus,
    refresh: refreshBookingSearchIndex,
  } = useLocationSearchIndex({ mode: bookingIndexMode, enabled: bookingSearchEnabled });
  const safeBookingIndex = useMemo(
    () =>
      bookingSearchIndex || {
        sections: {
          suggested: [],
          district: [],
          destination: [],
          camp: [],
          safari: [],
          cruise: [],
          experience: [],
        },
        items: [],
        lookup: {
          destinationBySlug: new Map(),
          districtByName: new Map(),
          destinationsByDistrict: new Map(),
          campsByDistrict: new Map(),
          campsByDestination: new Map(),
          safarisByDistrict: new Map(),
          safarisByDestination: new Map(),
          cruisesByDistrict: new Map(),
          cruisesByDestination: new Map(),
          experiencesByDistrict: new Map(),
          experiencesByDestination: new Map(),
        },
        meta: {},
      },
    [bookingSearchIndex],
  );
  const searchableExperienceCategoryKeys = useMemo(() => {
    if (bookingSearchStatus !== "success") return null;
    const sourceCategories =
      experienceCategoryMaster.length
        ? experienceCategoryMaster
        : Array.isArray(bookingSearchIndex?.meta?.categories)
        ? bookingSearchIndex.meta.categories
        : [];
    const categoryKeysByType = {
      safari: new Set(),
      cruise: new Set(),
      trek: new Set([TREK_CATEGORY.slug]),
    };
    sourceCategories.forEach((entry) => {
      const key = normalizeExperienceCategoryKey(entry?.slug || entry?.key || entry?.id || "");
      if (!key) return;
      const type =
        inferExperienceTypeFromTemplate(entry?.templateType) ||
        inferExperienceTypeFromCategory(key);
      if (!categoryKeysByType[type]) {
        categoryKeysByType[type] = new Set();
      }
      categoryKeysByType[type].add(key);
    });
    const keys = new Set();
    (safeBookingIndex?.items || []).forEach((item) => {
      const categoryKey = normalizeExperienceCategoryKey(item?.categorySlug || item?.category || "");
      if (categoryKey) {
        keys.add(categoryKey);
        return;
      }
      const inferredType = normalizeExperienceType(item?.experienceType || item?.type || "");
      const fallbackKeys = categoryKeysByType[inferredType] || new Set();
      if (fallbackKeys.size) {
        fallbackKeys.forEach((entryKey) => keys.add(entryKey));
        return;
      }
      if (inferredType === "trek") keys.add(TREK_CATEGORY.slug);
    });
    return keys;
  }, [
    bookingSearchIndex?.meta?.categories,
    bookingSearchStatus,
    experienceCategoryMaster,
    safeBookingIndex,
  ]);
  const destinationItems = useMemo(() => {
    const items = menuSearchIndex?.sections?.destination || [];
    return [...items].sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
  }, [menuSearchIndex]);
  const districtItems = useMemo(() => {
    const items = menuSearchIndex?.sections?.district || [];
    return [...items].sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
  }, [menuSearchIndex]);
  const destinationLookup = menuSearchIndex?.lookup?.destinationBySlug || new Map();
  const destinationsByDistrict = menuSearchIndex?.lookup?.destinationsByDistrict || new Map();
  const campsByDestination = menuSearchIndex?.lookup?.campsByDestination || new Map();
  const safarisByDestination = menuSearchIndex?.lookup?.safarisByDestination || new Map();
  const cruisesByDestination = menuSearchIndex?.lookup?.cruisesByDestination || new Map();
  const experiencesByDestination = menuSearchIndex?.lookup?.experiencesByDestination || new Map();

  const menuAreaClassName = useMemo(() => {
    const padding = isSticky ? "py-0" : "py-0.5 md:py-1";
    const initialLandingClass =
      enableLandingNav && routeLandingMode && !isSticky
        ? "ecotour-site-header__surface--landing-initial"
        : "";
    const base = `${UI.menuAreaBase} ${padding} ${initialLandingClass}`;
    if (isSticky) return `${base} border-slate-100 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.08)]`;
    if (isOverlayActive) return `${base} border-transparent bg-white/80 backdrop-blur-md`;
    return `${base} border-slate-100 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.08)]`;
  }, [enableLandingNav, isSticky, isOverlayActive, routeLandingMode]);

  const headerClassName = useMemo(() => {
    const base = "ecotour-site-header absolute left-0 top-0 z-50 w-full";
    return isOverlayActive ? `${base} bg-transparent` : `${base} bg-white`;
  }, [isOverlayActive]);

  const stickyWrapperClassName = useMemo(() => {
    const base = "transition-all duration-300 ease-soft";
    if (isSticky) return `${base} fixed left-0 right-0 top-0`;
    if (isOverlayActive) return `${base} absolute left-0 right-0 top-0`;
    return `${base} relative`;
  }, [isSticky, isOverlayActive]);

  const locationContext = useMemo(() => {
    if (locationSelection.destinationSlug) {
      return { type: "destination", destinationSlug: locationSelection.destinationSlug, district: locationSelection.district };
    }
    if (locationSelection.district) return { type: "district", district: locationSelection.district };
    return { type: "all" };
  }, [locationSelection.destinationSlug, locationSelection.district]);

  const locationSummary = useMemo(() => {
    if (locationSelection.location && locationSelection.destinationSlug) return `${locationSelection.location}`;
    if (locationSelection.destinationSlug) {
      const destination =
        bookingSearchIndex?.lookup?.destinationBySlug?.get(locationSelection.destinationSlug) ||
        menuSearchIndex?.lookup?.destinationBySlug?.get(locationSelection.destinationSlug);
      return (
        locationSelection.destinationLabel ||
        destination?.label ||
        humanizeDestinationSlug(locationSelection.destinationSlug) ||
        "Select destination"
      );
    }
    if (locationSelection.district) return locationSelection.district;
    return "Where to?";
  }, [
    bookingSearchIndex,
    locationSelection.destinationLabel,
    locationSelection.destinationSlug,
    locationSelection.district,
    locationSelection.location,
    menuSearchIndex,
  ]);

  const bookingCategories = useMemo(() => {
    const raw =
      experienceCategoryMaster.length
        ? experienceCategoryMaster
        : Array.isArray(bookingSearchIndex?.meta?.categories)
        ? bookingSearchIndex.meta.categories
        : [];
    const deduped = new Map();
    raw.forEach((entry) => {
      const sourceSlug = entry?.slug || entry?.key || entry?.id || "";
      const slug = normalizeExperienceCategoryKey(sourceSlug);
      if (!slug) return;
      const existing = deduped.get(slug);
      const normalizedEntry = {
        key: slug,
        slug,
        label:
          slug === TREK_CATEGORY.slug
            ? TREK_CATEGORY.label
            : (entry?.label || entry?.name || sourceSlug || "Category"),
        icon: entry?.icon || (slug === TREK_CATEGORY.slug ? TREK_CATEGORY.icon : "fa-solid fa-compass"),
        description:
          entry?.description || (slug === TREK_CATEGORY.slug ? TREK_CATEGORY.description : ""),
        templateType: entry?.templateType || "",
        type: entry?.type || "master",
      };
      if (!existing) {
        deduped.set(slug, normalizedEntry);
        return;
      }
      deduped.set(slug, {
        ...existing,
        label: existing.label || normalizedEntry.label,
        icon: existing.icon || normalizedEntry.icon,
        description: existing.description || normalizedEntry.description,
      });
    });
    const normalized = Array.from(deduped.values());
    const hasTrek = normalized.some((entry) => entry.slug === TREK_CATEGORY.slug);
    if (!hasTrek && (!searchableExperienceCategoryKeys || searchableExperienceCategoryKeys.has(TREK_CATEGORY.slug))) {
      normalized.push(TREK_CATEGORY);
    }
    if (!searchableExperienceCategoryKeys) return normalized;
    return normalized.filter((entry) => searchableExperienceCategoryKeys.has(entry.slug));
  }, [bookingSearchIndex?.meta?.categories, experienceCategoryMaster, searchableExperienceCategoryKeys]);

  const selectedExperienceCategory = useMemo(() => {
    const normalizedSelection = normalizeExperienceCategoryKey(experienceCategory);
    if (!bookingCategories.length) {
      if (!normalizedSelection) return null;
      if (normalizedSelection === TREK_CATEGORY.slug) return TREK_CATEGORY;
      return { slug: normalizedSelection, label: normalizedSelection };
    }
    const match =
      bookingCategories.find((entry) => entry.slug === normalizedSelection) || bookingCategories[0];
    return match || null;
  }, [bookingCategories, experienceCategory]);

  const experienceSummary = useMemo(() => {
    return selectedExperienceCategory?.label || "Category";
  }, [selectedExperienceCategory]);

  const hasLocationSelection = useMemo(
    () =>
      Boolean(
        locationSelection.district ||
          locationSelection.destinationSlug ||
          locationSelection.location ||
          locationSelection.campSlug
      ),
    [
      locationSelection.destinationSlug,
      locationSelection.district,
      locationSelection.location,
      locationSelection.campSlug,
    ],
  );

  const dateSummary = useMemo(
    () => formatDateRangeLabel(dateRange.startDate, dateRange.endDate, searchMode === "experiences"),
    [dateRange.endDate, dateRange.startDate, searchMode],
  );
  const guestSummary = useMemo(() => {
    if (searchMode !== "stays") return "";
    const adults = Number(guestMix?.adults ?? 1) || 1;
    const children = Number(guestMix?.children ?? 0) || 0;
    const infants = Number(guestMix?.infants ?? 0) || 0;
    const total = adults + children + infants;
    return `${total} guest${total > 1 ? "s" : ""}`;
  }, [guestMix?.adults, guestMix?.children, guestMix?.infants, searchMode]);

  const isHomeLandingRoute = Boolean(routeLandingMode);
  const showLandingNav = enableLandingNav && isHomeLandingRoute && !isSticky;
  const showLandingSearch = showLandingNav && activeLandingMode !== "explore" && searchMode !== "itinerary";

  const searchButtonLabel = useMemo(() => {
    if (searchMode === "experiences") {
      return `Search ${selectedExperienceCategory?.label || "experiences"}`;
    }
    if (searchMode === "itinerary") return "Start itinerary planning";
    return "Search stays";
  }, [searchMode, selectedExperienceCategory]);

  useEffect(() => {
    if (searchMode !== "experiences") return;
    const nextCategorySlug = selectedExperienceCategory?.slug || "";
    if (!nextCategorySlug) return;
    if (nextCategorySlug && nextCategorySlug !== experienceCategory) {
      dispatch(setSearchExperienceCategory(nextCategorySlug));
    }
    const nextType =
      inferExperienceTypeFromTemplate(selectedExperienceCategory?.templateType) ||
      inferExperienceTypeFromCategory(nextCategorySlug);
    if (experienceType !== nextType) {
      dispatch(setSearchExperienceType(nextType));
    }
  }, [
    dispatch,
    experienceCategory,
    experienceType,
    searchMode,
    selectedExperienceCategory?.slug,
    selectedExperienceCategory?.templateType,
  ]);

  const normalizeDestinationSlug = (entry) =>
    String(entry?.destinationSlug || entry?.slug || "").trim();

  const resolveFeedCount = (payload) => {
    const total = payload?.meta?.total;
    if (Number.isFinite(total)) return total;
    const items = payload?.data?.items;
    return Array.isArray(items) ? items.length : 0;
  };

  const resolveDestinationLabel = useCallback(
    (slug) => {
      if (!slug) return "";
      const entry = destinationLookup?.get?.(slug);
      if (entry?.label) return entry.label;
      const fallback = destinationItems.find((item) => normalizeDestinationSlug(item) === slug);
      return fallback?.label || slug;
    },
    [destinationItems, destinationLookup],
  );

  const resolveCampSlug = (camp) => resolveCampSlugFromEntry(camp);

  const resolveExperienceSlug = (entry) => {
    if (!entry) return "";
    if (typeof entry.experienceSlug === "string") return entry.experienceSlug;
    if (typeof entry.slug === "string") return entry.slug;
    if (typeof entry.id === "string" && entry.id.includes(":")) {
      return entry.id.split(":").pop() || "";
    }
    return "";
  };

  const requestPortalAuth = useCallback((detail = {}) => {
    const normalizedDetail =
      detail && typeof detail === "object"
        ? detail
        : { reason: String(detail || "login") };
    authRequestSequenceRef.current += 1;
    setIsMenuOpen(false);
    setPendingAuthRequest({
      id: authRequestSequenceRef.current,
      detail: normalizedDetail,
    });
  }, []);

  const handleAuthAction = useCallback(
    (reason = "login") => {
      setIsSideMenuOpen(true);
      requestPortalAuth({ reason });
    },
    [requestPortalAuth],
  );

  const handleAuthRequestHandled = useCallback((requestId) => {
    setPendingAuthRequest((current) => {
      if (!current) return current;
      if (requestId != null && current.id !== requestId) return current;
      return null;
    });
  }, []);

  const handleSideMenuClose = useCallback(() => {
    setIsSideMenuOpen(false);
    setPendingAuthRequest(null);
  }, []);

  const handleLogout = useCallback(() => {
    clearSession();
    clearTokens();
    dispatch(clearUserBookings());
    setSession(null);
    setIsMenuOpen(false);
  }, [dispatch]);

  const landingNavClassName = useMemo(
    () =>
      `transition-all duration-300 ease-soft ${
        showLandingNav ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none h-0 overflow-hidden"
      }`,
    [showLandingNav],
  );

  const defaultNavClassName = useMemo(
    () =>
      `transition-all duration-300 ease-soft ${
        showLandingNav ? "opacity-0 translate-y-2 pointer-events-none h-0 overflow-hidden" : "opacity-100 translate-y-0"
      }`,
    [showLandingNav],
  );


  // ======= handlers =======

  const coerceDateRange = useCallback(
    (range) => {
      if (!range) return { startDate: null, endDate: null };
      let nextStart = range.startDate || null;
      let nextEnd = range.endDate || null;
      if (searchMode === "stays" && nextStart) {
        if (nextEnd && nextEnd <= nextStart) nextEnd = null;
      }
      return { startDate: nextStart || null, endDate: nextEnd || null };
    },
    [searchMode],
  );

  const handleDateChange = useCallback(
    (range) => {
      if (!range) return;
      const nextRange = coerceDateRange(range);
      setDateRange({
        startDate: nextRange.startDate || null,
        endDate: nextRange.endDate || null,
      });
      const formattedStart = formatDateParam(nextRange.startDate || null);
      const formattedEnd = formatDateParam(nextRange.endDate || null);
      dispatch(
        setSearchDates({
          startDate: formattedStart,
          endDate: formattedEnd,
        }),
      );
      trackEvent("search_date_change", {
        mode: searchMode,
        startDate: formattedStart,
        endDate: formattedEnd,
      });
    },
    [coerceDateRange, dispatch, searchMode, trackEvent],
  );

  const handleGuestChange = useCallback(
    (next) => {
      const normalized = normalizeGuestMix(next);
      setGuestMix(normalized);
      dispatch(setSearchGuests(normalized));
    },
    [dispatch],
  );

  const handleExperienceCategoryChange = useCallback(
    (nextCategory) => {
      const normalized =
        typeof nextCategory === "string"
          ? normalizeExperienceCategory(nextCategory)
          : normalizeExperienceCategory(nextCategory?.slug || nextCategory?.key);
      if (!normalized) return;
      const inferredType =
        inferExperienceTypeFromTemplate(nextCategory?.templateType) ||
        inferExperienceTypeFromCategory(normalized);
      dispatch(setSearchExperienceCategory(normalized));
      dispatch(setSearchExperienceType(inferredType));
      setIsExperienceOpen(false);
      trackEvent("search_category_change", { mode: "experiences", category: normalized });
    },
    [dispatch, trackEvent],
  );

  const handleModeChange = useCallback(
    (nextMode) => {
      const normalizedLandingMode = normalizeHomeLandingMode(nextMode);
      const normalizedMode = isBookingLandingMode(normalizedLandingMode)
        ? normalizedLandingMode
        : normalizeSearchMode(nextMode);

      if (normalizedLandingMode !== "explore") {
        dispatch(setSearchMode(normalizedMode));
      }
      setIsLocationOpen(false);
      setIsDateOpen(false);
      setIsExperienceOpen(false);
      if (enableLandingNav && isHomeLandingRoute && normalizedLandingMode) {
        const nextPath = resolveHomeLandingRoute(normalizedLandingMode);
        if (location.pathname !== nextPath) {
          const params = new URLSearchParams(location.search || "");
          params.delete("mode");
          params.delete("section");
          const nextSearch = params.toString();
          navigate({
            pathname: nextPath,
            search: nextSearch ? `?${nextSearch}` : "",
          });
        }
      }
      trackEvent("search_mode_change", { mode: normalizedLandingMode || normalizedMode });
    },
    [dispatch, enableLandingNav, isHomeLandingRoute, location.pathname, location.search, navigate, trackEvent],
  );

  const openSearchWizard = useCallback((initialSheet = "main") => {
    if (!isSearchViewportCompact) return;
    setIsSearchWizardOpen(true);
    setMobileSheet(initialSheet);
    wizardCompletedRef.current = false;
    trackEvent("search_wizard_open", { mode: searchMode, sheet: initialSheet });
  }, [isSearchViewportCompact, searchMode, trackEvent]);

  const handleHeaderSearchIconClick = useCallback(() => {
    if (isSearchViewportCompact) {
      openSearchWizard();
      return;
    }
    setIsMenuOpen(false);
    setIsExperienceOpen(false);
    setIsDateOpen(false);
    setIsLocationOpen((prev) => !prev);
  }, [isSearchViewportCompact, openSearchWizard]);

  const handleLocationSelect = useCallback(
    (selection) => {
      if (!selection) return {};
      const resolvedType = normalizeSelectionType(selection);
      const isExperienceSelection =
        resolvedType === "safari" ||
        resolvedType === "cruise" ||
        resolvedType === "trek" ||
        resolvedType === "experience" ||
        Boolean(selection.experienceSlug || selection.experiencePath || selection.slug || selection.href);
      const resolvedExperienceType = isExperienceSelection
        ? normalizeExperienceType(
            selection.experienceType ||
              selection.type ||
              inferExperienceTypeFromCategory(selection.categorySlug || selection.categoryLabel || ""),
          )
        : "";
      const nextSelection = {
        district: selection.district || "",
        destinationSlug: selection.destinationSlug || "",
        destinationLabel:
          resolvedType === "destination"
            ? selection.label || selection.destinationLabel || ""
            : selection.destinationLabel || "",
        location: resolvedType === "camp" ? selection.location || selection.label || "" : "",
        campSlug: resolvedType === "camp" ? resolveCampSlugFromEntry(selection) : "",
        experienceType: resolvedExperienceType,
        experienceSlug: isExperienceSelection ? resolveExperienceSlug(selection) : "",
        experiencePath: isExperienceSelection ? selection.experiencePath || selection.path || selection.href || "" : "",
        experienceCategory: isExperienceSelection ? normalizeExperienceCategory(selection.categorySlug || "") : "",
      };
      setLocationSelection(nextSelection);
      setLocationQuery("");
      dispatch(setSearchLocation(nextSelection));
      trackEvent("search_location_select", {
        mode: searchMode,
        type: resolvedType || selection.type || "",
        label: selection.label || "",
        destinationSlug: selection.destinationSlug || "",
        district: selection.district || "",
        experienceSlug: nextSelection.experienceSlug || "",
      });
      if (searchMode === "experiences") {
        const nextCategory = normalizeExperienceCategory(selection.categorySlug || "");
        if (nextCategory) {
          dispatch(setSearchExperienceCategory(nextCategory));
          dispatch(setSearchExperienceType(inferExperienceTypeFromCategory(nextCategory)));
        } else if (resolvedType === "trek") {
          dispatch(setSearchExperienceCategory(TREK_CATEGORY.slug));
          dispatch(setSearchExperienceType("trek"));
        } else if (resolvedType === "cruise") {
          dispatch(setSearchExperienceType("cruise"));
        } else if (resolvedType === "safari") {
          dispatch(setSearchExperienceType("safari"));
        }
      }
      if (resolvedType !== "suggested") setIsLocationOpen(false);
      if (resolvedType === "suggested") return { keepOpen: true, prefillQuery: selection.label };
      return {};
    },
    [dispatch, searchMode],
  );

  const handleMobileLocationPick = useCallback(
    (item) => {
      handleLocationSelect(item);
      if (item?.type === "destination") {
        setWizardLocationQuery("");
      }
      setMobileSheet("main");
    },
    [handleLocationSelect],
  );

  const handleLocationClear = useCallback((options = {}) => {
    const cleared = {
      district: "",
      destinationSlug: "",
      destinationLabel: "",
      location: "",
      campSlug: "",
      experienceType: "",
      experienceSlug: "",
      experiencePath: "",
      experienceCategory: "",
    };
    setLocationSelection(cleared);
    setLocationQuery("");
    dispatch(setSearchLocation(cleared));
    if (!options.keepOpen) {
      setIsLocationOpen(false);
    }
  }, [dispatch]);

  const handleResetSearch = useCallback(() => {
    dispatch(clearSearchState());
    setLocationSelection({
      district: "",
      destinationSlug: "",
      destinationLabel: "",
      location: "",
      campSlug: "",
      experienceType: "",
      experienceSlug: "",
      experiencePath: "",
      experienceCategory: "",
    });
    setDateRange({ startDate: null, endDate: null });
    setGuestMix(normalizeGuestMix(DEFAULT_GUESTS));
    setItineraryInputs({ origin: "", destination: "" });
    setWizardLocationQuery("");
    setMobileSheet("main");
    setMobileDraftDates(null);
    setIsExperienceOpen(false);
    setIsLocationOpen(false);
    setIsDateOpen(false);
    trackEvent("search_reset", { mode: searchMode });
  }, [dispatch, searchMode, trackEvent]);

  const buildSearchParams = useCallback(
    (mode = searchMode, itineraryPayload = {}, dateRangeOverride = null) => {
      const params = new URLSearchParams();
      const categorySlug = selectedExperienceCategory?.slug || experienceCategory;
      const effectiveDateRange = dateRangeOverride || {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      if (mode) params.set("mode", mode);
      if (effectiveDateRange.startDate) {
        params.set("checkIn", formatDateParam(effectiveDateRange.startDate));
      }
      if (effectiveDateRange.endDate) {
        params.set("checkOut", formatDateParam(effectiveDateRange.endDate));
      }
      if (guestMix.adults) params.set("adults", String(guestMix.adults));
      if (guestMix.children) params.set("children", String(guestMix.children));
      if (guestMix.infants) params.set("infants", String(guestMix.infants));
      if (mode === "itinerary") {
        if (itineraryPayload.origin) params.set("from", itineraryPayload.origin);
        if (itineraryPayload.destination) params.set("to", itineraryPayload.destination);
        params.set("section", "itinerary-planner");
      }
      if (mode === "experiences") {
        params.set("xpTab", experienceType);
        if (categorySlug) params.set("xpCategory", categorySlug);
        if (effectiveDateRange.startDate) params.set("date", formatDateParam(effectiveDateRange.startDate));
        if (locationSelection?.destinationSlug) params.set("destinationSlug", locationSelection.destinationSlug);
        if (locationSelection?.district) params.set("district", locationSelection.district);
        if (locationSelection?.campSlug) {
          const scopedCampSlug = locationSelection?.destinationSlug
            ? `${locationSelection.destinationSlug}:${locationSelection.campSlug}`
            : locationSelection.campSlug;
          params.set("campSlug", scopedCampSlug);
        }
        params.set("section", EXPERIENCE_SECTION_ID);
      }
      if (mode === "stays") {
        params.set("section", STAY_SECTION_ID);
        if (locationSelection?.destinationSlug) params.set("destinationSlug", locationSelection.destinationSlug);
        if (locationSelection?.district) params.set("district", locationSelection.district);
        if (locationSelection?.campSlug) params.set("campSlug", locationSelection.campSlug);
      }
      return params;
    },
    [
      dateRange.endDate,
      dateRange.startDate,
      experienceCategory,
      experienceType,
      guestMix.adults,
      guestMix.children,
      guestMix.infants,
      searchMode,
      selectedExperienceCategory?.slug,
      locationSelection?.destinationSlug,
      locationSelection?.district,
      locationSelection?.campSlug,
    ],
  );

  const handleSearch = useCallback((searchOptions = {}) => {
    const effectiveDateRange = searchOptions?.dateRange || {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    const hasCamp = Boolean(
      (locationSelection.location || locationSelection.campSlug) && locationSelection.destinationSlug
    );
    const hasDestination = Boolean(locationSelection.destinationSlug);
    const hasDistrict = Boolean(locationSelection.district);
    const mode = searchMode || "stays";
    const nextExperienceType = experienceType || "safari";
    const nextExperienceCategory =
      selectedExperienceCategory?.slug ||
      experienceCategory ||
      (nextExperienceType === TREK_CATEGORY.slug ? TREK_CATEGORY.slug : "");
    const itineraryPayload = {
      origin: itineraryInputs.origin || "",
      destination: itineraryInputs.destination || "",
    };

    dispatch(
      setSearchState({
        mode,
        experienceType: nextExperienceType,
        experienceCategory: nextExperienceCategory,
        startDate: formatDateParam(effectiveDateRange.startDate),
        endDate: formatDateParam(effectiveDateRange.endDate),
        guests: guestMix,
        location: locationSelection,
        itinerary: itineraryPayload,
      }),
    );

    setIsLocationOpen(false);
    setIsDateOpen(false);

    trackEvent("search_submit", {
      mode,
      experienceType: nextExperienceType,
      experienceCategory: nextExperienceCategory,
      startDate: formatDateParam(effectiveDateRange.startDate),
      endDate: formatDateParam(effectiveDateRange.endDate),
      guests: guestMix,
      location: locationSelection,
      itinerary: itineraryPayload,
    });

    const params = buildSearchParams(mode, itineraryPayload, effectiveDateRange);
    const search = params.toString();

    const navigateWithParams = (pathname, options = {}) => {
      const hash = options.hash || "";
      if (search) navigate({ pathname, search, hash });
      else if (hash) navigate({ pathname, hash });
      else navigate(pathname);
    };

    if (mode === "itinerary") {
      if (location.pathname !== "/") {
        navigateWithParams("/");
        return;
      }
      const scrollToPlanner = () => {
        const target = document.getElementById("itinerary-planner");
        if (!target) return false;
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      };
      if (!scrollToPlanner()) {
        window.setTimeout(scrollToPlanner, 120);
      }
      return;
    }

    if (mode === "experiences") {
      const selectedExperiencePath = String(locationSelection.experiencePath || "").trim();
      const selectedExperienceSlug = String(locationSelection.experienceSlug || "").trim();
      const selectedExperienceType = normalizeExperienceType(
        locationSelection.experienceType ||
          nextExperienceType ||
          inferExperienceTypeFromCategory(locationSelection.experienceCategory || nextExperienceCategory),
      );
      if (selectedExperiencePath) {
        navigateWithParams(selectedExperiencePath);
        return;
      }
      if (selectedExperienceSlug && locationSelection.destinationSlug) {
        if (selectedExperienceType === "cruise") {
          navigateWithParams(`/cruise-boating/${locationSelection.destinationSlug}/${selectedExperienceSlug}`);
          return;
        }
        if (selectedExperienceType === "trek") {
          navigateWithParams(`/treks-trails/${locationSelection.destinationSlug}/${selectedExperienceSlug}`);
          return;
        }
        navigateWithParams(`/experiences/${locationSelection.destinationSlug}/${selectedExperienceSlug}`);
        return;
      }
      if (nextExperienceType === "cruise") {
        navigateWithParams("/cruise-boating");
        return;
      }
      if (nextExperienceType === "trek") {
        navigateWithParams("/treks-trails");
        return;
      }
      navigateWithParams("/experiences");
      return;
    }

    if (hasCamp && mode !== "experiences") {
      const campSlug = locationSelection.campSlug || slugify(locationSelection.location);
      if (campSlug) {
        navigateWithParams(`/stays-camps/${locationSelection.destinationSlug}/${campSlug}`);
        return;
      }
    }

    if (hasDestination) {
      navigateWithParams(`/destination/${locationSelection.destinationSlug}`);
      return;
    }

    if (hasDistrict) {
      const districtSlug = canonicalizeDistrictSlug(locationSelection.district);
      if (districtSlug) {
        navigateWithParams(`/district/${districtSlug}`);
        return;
      }
    }

    navigate("/stays-camps");
  }, [
    buildSearchParams,
    dateRange.endDate,
    dateRange.startDate,
    dispatch,
    experienceCategory,
    experienceType,
    guestMix,
    itineraryInputs.destination,
    itineraryInputs.origin,
    location.pathname,
    locationSelection,
    navigate,
    searchMode,
    selectedExperienceCategory?.slug,
    trackEvent,
  ]);

  const handleSearchFromWizard = useCallback(() => {
    let effectiveDateRange = dateRange;
    if (mobileSheet === "date") {
      const nextDateRange = coerceDateRange(mobileDraftDates || dateRange);
      effectiveDateRange = {
        startDate: nextDateRange.startDate || dateRange.startDate || null,
        endDate: nextDateRange.endDate || dateRange.endDate || null,
      };
      handleDateChange(effectiveDateRange);
    }
    wizardCompletedRef.current = true;
    handleSearch({ dateRange: effectiveDateRange });
    setIsSearchWizardOpen(false);
    setMobileSheet("main");
  }, [coerceDateRange, dateRange, handleDateChange, handleSearch, mobileDraftDates, mobileSheet]);

  const handleItineraryOriginChange = useCallback(
    (value) => {
      setItineraryInputs((prev) => ({ ...prev, origin: value }));
    },
    [],
  );

  const handleItineraryDestinationChange = useCallback(
    (value) => {
      setItineraryInputs((prev) => ({ ...prev, destination: value }));
    },
    [],
  );

  const renderExperiencePopover = (popoverId) =>
    isExperienceOpen ? (
      <div id={popoverId} ref={experiencePopoverRef} className={`${UI.popoverCenter} w-[min(92vw,560px)]`}>
        <div className="border-b border-slate-100 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">Category</p>
          <p className="text-sm font-semibold text-slate-900">Choose the experience category</p>
        </div>
        <div className="grid gap-2 p-3 sm:grid-cols-3">
          {bookingCategories.map((entry) => {
            const key = entry.slug || entry.key;
            const isActive = key === selectedExperienceCategory?.slug;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleExperienceCategoryChange(entry)}
                aria-pressed={isActive}
                className={`group flex h-full flex-col gap-1 rounded-2xl border px-3 py-3 text-left transition ${
                  isActive ? "border-slate-900 bg-slate-900 text-white" : "border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50"
                }`}
              >
                <span
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-xl ${
                    isActive ? "bg-white/15 text-white" : "bg-slate-50 text-slate-700"
                  }`}
                >
                  <i className={entry.icon} aria-hidden="true" />
                </span>
                <span className="text-sm font-semibold">{entry.label}</span>
                <span className={`text-[11px] ${isActive ? "text-white/70" : "text-slate-500"}`}>{entry.description}</span>
              </button>
            );
          })}
        </div>
      </div>
    ) : null;
  const showHeaderPopovers = !isSearchViewportCompact && !isSearchWizardOpen;
  const sheetTitle =
    searchMode === "experiences"
      ? "Plan an experience"
      : searchMode === "itinerary"
      ? "Plan an itinerary"
      : "Plan your stay";
  const isExperienceMode = searchMode === "experiences";
  const showGuestMix = searchMode === "stays";
  const bookingSearchErrorMessage =
    bookingSearchStatus === "error"
      ? bookingSearchError?.message || "Unable to load live suggestions. Please retry."
      : "";
  const desktopSearchEnabled = Boolean(locationQuery.trim());
  const desktopSearchActive = isLocationOpen && !isMobile && showHeaderPopovers;
  const desktopSearchContext = useMemo(() => {
    if (locationQuery.trim()) return { type: "all" };
    if (locationSelection.destinationSlug) {
      return { type: "destination", destinationSlug: locationSelection.destinationSlug, district: locationSelection.district };
    }
    if (locationSelection.district) return { type: "district", district: locationSelection.district };
    return { type: "all" };
  }, [locationQuery, locationSelection.destinationSlug, locationSelection.district]);
  const { sections: remoteLocationSections, isLoading: remoteLocationLoading } = useLocationSearchRemote({
    query: locationQuery,
    mode: bookingIndexMode,
    context: desktopSearchContext,
    experienceCategory: selectedExperienceCategory?.slug || "",
    limit: 40,
    enabled: desktopSearchEnabled && desktopSearchActive,
  });
  const {
    sections: defaultLocationSections,
    isLoading: defaultLocationLoading,
  } = useLocationSearchDefaults({
    mode: bookingIndexMode,
    enabled: !desktopSearchEnabled && desktopSearchActive,
  });
  const desktopLoading = desktopSearchEnabled ? remoteLocationLoading : defaultLocationLoading;
  const shouldShowSpecificPrompt =
    searchMode === "stays" &&
    Boolean(locationSelection.destinationSlug) &&
    !locationSelection.location;
  const mergeDistrictSections = useCallback(
    (sections, query, context) => {
      const primary = Array.isArray(sections) ? sections : [];
      const hasDistricts = primary.some((section) => {
        if (String(section?.key || "").toLowerCase().includes("district")) return true;
        const items = Array.isArray(section?.items) ? section.items : [];
        return items.some((item) => String(item?.type || "").toLowerCase() === "district");
      });
      if (hasDistricts) return primary;
      const fallbackSections = filterLocationResults(safeBookingIndex, query || "", context, {
        mode: bookingIndexMode,
        experienceCategory: selectedExperienceCategory?.slug || "",
      });
      const districtSections = (fallbackSections || []).filter((section) => {
        if (String(section?.key || "").toLowerCase().includes("district")) return true;
        const items = Array.isArray(section?.items) ? section.items : [];
        return items.some((item) => String(item?.type || "").toLowerCase() === "district");
      });
      if (!districtSections.length) return primary;
      return [...primary, ...districtSections];
    },
    [bookingIndexMode, safeBookingIndex, selectedExperienceCategory?.slug],
  );
  const wizardSearchContext = useMemo(() => {
    const trimmed = wizardLocationQuery.trim();
    if (trimmed) return { type: "all" };
    if (shouldShowSpecificPrompt && locationSelection.destinationSlug) {
      return { type: "destination", destinationSlug: locationSelection.destinationSlug };
    }
    if (locationSelection.district) return { type: "district", district: locationSelection.district };
    return { type: "all" };
  }, [locationSelection.destinationSlug, locationSelection.district, shouldShowSpecificPrompt, wizardLocationQuery]);

  const handleResultsMeta = useCallback(
    ({ hasResults, query }) => {
      if (hasResults || !query) return;
      const last = lastNoResultsRef.current;
      if (last.query === query && last.mode === bookingIndexMode) return;
      lastNoResultsRef.current = { query, mode: bookingIndexMode };
      trackEvent("search_no_results", {
        mode: bookingIndexMode,
        query,
        category: selectedExperienceCategory?.slug || "",
      });
    },
    [bookingIndexMode, selectedExperienceCategory?.slug, trackEvent],
  );

  const wizardSearchEnabled = Boolean(wizardLocationQuery.trim());
  const wizardSearchActive = isSearchWizardOpen && mobileSheet === "location";
  const { sections: remoteWizardSections, isLoading: remoteWizardLoading } = useLocationSearchRemote({
    query: wizardLocationQuery,
    mode: bookingIndexMode,
    context: wizardSearchContext,
    experienceCategory: selectedExperienceCategory?.slug || "",
    limit: 40,
    enabled: wizardSearchEnabled && wizardSearchActive,
  });
  const {
    sections: defaultWizardSections,
    isLoading: defaultWizardLoading,
  } = useLocationSearchDefaults({
    mode: bookingIndexMode,
    enabled: !wizardSearchEnabled && wizardSearchActive,
  });

  const wizardLocationResults = useMemo(() => {
    if (!wizardSearchEnabled) {
      if (wizardSearchContext?.type && wizardSearchContext.type !== "all") {
        return filterLocationResults(safeBookingIndex, "", wizardSearchContext, {
          mode: bookingIndexMode,
          experienceCategory: selectedExperienceCategory?.slug || "",
        });
      }
      return mergeDistrictSections(defaultWizardSections, "", wizardSearchContext);
    }
    if (!Array.isArray(remoteWizardSections)) return [];
    return mergeDistrictSections(remoteWizardSections, wizardLocationQuery, wizardSearchContext);
  }, [
    bookingIndexMode,
    defaultWizardSections,
    mergeDistrictSections,
    remoteWizardSections,
    safeBookingIndex,
    selectedExperienceCategory?.slug,
    wizardLocationQuery,
    wizardSearchContext,
    wizardSearchEnabled,
  ]);
  const desktopResultsWithDistricts = useMemo(
    () =>
      desktopSearchEnabled
        ? mergeDistrictSections(remoteLocationSections, locationQuery, desktopSearchContext)
        : mergeDistrictSections(defaultLocationSections, "", desktopSearchContext),
    [
      defaultLocationSections,
      desktopSearchContext,
      desktopSearchEnabled,
      locationQuery,
      mergeDistrictSections,
      remoteLocationSections,
    ],
  );

  const wizardResultsToRender = useMemo(
    () => (wizardLocationResults || []).filter((section) => (section.items || []).length > 0),
    [wizardLocationResults],
  );
  const wizardLoading =
    bookingSearchStatus === "loading" ||
    (wizardSearchActive && wizardSearchEnabled && remoteWizardLoading) ||
    (wizardSearchActive && !wizardSearchEnabled && defaultWizardLoading) ||
    isWizardSearching;

  useEffect(() => {
    if (!isSearchWizardOpen) return;
    if (mobileSheet !== "location") return;
    if (bookingSearchStatus === "loading") return;
    setIsWizardSearching(true);
    const timer = window.setTimeout(() => setIsWizardSearching(false), 180);
    return () => window.clearTimeout(timer);
  }, [bookingSearchStatus, isSearchWizardOpen, mobileSheet, wizardLocationQuery]);

  useEffect(() => {
    if (!isSearchWizardOpen) return;
    if (mobileSheet !== "location") return;
    const trimmed = wizardLocationQuery.trim();
    const hasResults = wizardLocationResults.some((section) => (section.items || []).length > 0);
    handleResultsMeta({ hasResults, query: trimmed });
  }, [handleResultsMeta, isSearchWizardOpen, mobileSheet, wizardLocationQuery, wizardLocationResults]);

  useEffect(() => {
    if (!isSearchWizardOpen) return;
    setWizardLocationQuery("");
  }, [isSearchWizardOpen, searchMode]);

  useEffect(() => {
    if (mobileSheet === "category" && searchMode !== "experiences") {
      setMobileSheet("main");
    }
  }, [mobileSheet, searchMode]);

  

  // ======= effects =======

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (searchPersistedRef.current) return;
    const params = new URLSearchParams(location.search || "");
    const hasRelevantParams = [
      "mode",
      "checkIn",
      "checkOut",
      "checkin",
      "checkout",
      "date",
      "xpTab",
      "xpCategory",
      "section",
      "adults",
      "adult",
      "children",
      "child",
      "infants",
      "infant",
      "from",
      "to",
      "destinationSlug",
      "district",
      "campSlug",
    ].some((key) => params.has(key));
    if (hasRelevantParams) {
      searchPersistedRef.current = true;
      return;
    }
    const raw = window.localStorage.getItem(LANDING_SEARCH_PERSIST_KEY);
    if (!raw) {
      searchPersistedRef.current = true;
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        dispatch(
          setSearchState({
            mode: parsed.mode,
            experienceType: parsed.experienceType,
            experienceCategory: parsed.experienceCategory,
            startDate: parsed?.dates?.startDate,
            endDate: parsed?.dates?.endDate,
            guests: parsed.guests,
            location: parsed.location,
            itinerary: parsed.itinerary,
          }),
        );
      }
    } catch (error) {
      window.localStorage.removeItem(LANDING_SEARCH_PERSIST_KEY);
    } finally {
      searchPersistedRef.current = true;
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    if (!syncLandingSearchMode) return;
    if (!routeSearchMode || landingSearch?.mode === routeSearchMode) return;
    dispatch(setSearchMode(routeSearchMode));
  }, [dispatch, landingSearch?.mode, routeSearchMode, syncLandingSearchMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!searchPersistedRef.current) return;
    window.localStorage.setItem(LANDING_SEARCH_PERSIST_KEY, JSON.stringify(landingSearch));
  }, [landingSearch]);

  // URL sync
  useEffect(() => {
    const search = location.search || "";
    if (lastSearchRef.current === search) return;
    lastSearchRef.current = search;

    const params = new URLSearchParams(search);
    const hasRelevantParams = [
      "mode",
      "checkIn",
      "checkOut",
      "checkin",
      "checkout",
      "date",
      "xpTab",
      "xpCategory",
      "section",
      "adults",
      "adult",
      "children",
      "child",
      "infants",
      "infant",
      "from",
      "to",
      "destinationSlug",
      "district",
      "campSlug",
    ].some(
      (key) => params.has(key),
    );
    if (!hasRelevantParams) return;

    const sectionParam = params.get("section") || "";
    const xpTab = params.get("xpTab") || "";
    const inferredMode =
      routeSearchMode ||
      params.get("mode") ||
      (sectionParam === EXPERIENCE_SECTION_ID || xpTab ? "experiences" : "") ||
      (sectionParam === STAY_SECTION_ID ? "stays" : "") ||
      searchMode;

    const nextMode = normalizeSearchMode(inferredMode);
    const nextExperienceType = xpTab ? normalizeExperienceType(xpTab) : experienceType;
    const nextExperienceCategory = normalizeExperienceCategory(
      params.get("xpCategory") || (nextExperienceType === "trek" ? "trek" : experienceCategory)
    );
    const startParam = params.get("checkIn") || params.get("checkin") || params.get("date") || "";
    const endParam = params.get("checkOut") || params.get("checkout") || "";

    const nextGuests = normalizeGuestMix({
      adults: params.get("adults") || params.get("adult"),
      children: params.get("children") || params.get("child"),
      infants: params.get("infants") || params.get("infant"),
    });
    const nextItinerary = {
      origin: params.get("from") || "",
      destination: params.get("to") || "",
    };
    const nextDestinationSlug = params.get("destinationSlug") || landingSearch?.location?.destinationSlug || "";
    const nextDistrict = params.get("district") || landingSearch?.location?.district || "";
    const nextCampSlug = normalizeCampSlug(params.get("campSlug") || landingSearch?.location?.campSlug || "", nextDestinationSlug);
    const nextLocation = {
      ...(landingSearch?.location || {}),
      district: nextDistrict,
      destinationSlug: nextDestinationSlug,
      destinationLabel:
        nextDestinationSlug === landingSearch?.location?.destinationSlug
          ? landingSearch?.location?.destinationLabel || ""
          : "",
      campSlug: nextCampSlug,
    };

    dispatch(
      setSearchState({
        mode: nextMode,
        experienceType: nextExperienceType,
        experienceCategory: nextExperienceCategory,
        startDate: startParam,
        endDate: endParam,
        guests: nextGuests,
        itinerary: nextItinerary,
        location: nextLocation,
      }),
    );

    setDateRange({ startDate: parseDateParam(startParam), endDate: parseDateParam(endParam) });
    setGuestMix(nextGuests);
  }, [dispatch, experienceCategory, experienceType, landingSearch?.location, location.search, routeSearchMode, searchMode]);

  // store -> local sync
  useEffect(() => {
    const nextStart = parseDateParam(landingSearch?.dates?.startDate);
    const nextEnd = parseDateParam(landingSearch?.dates?.endDate);
    setDateRange((prev) => {
      const prevStart = prev.startDate?.getTime?.() || null;
      const prevEnd = prev.endDate?.getTime?.() || null;
      const nextStartTime = nextStart?.getTime?.() || null;
      const nextEndTime = nextEnd?.getTime?.() || null;
      if (prevStart === nextStartTime && prevEnd === nextEndTime) return prev;
      return { startDate: nextStart, endDate: nextEnd };
    });
  }, [landingSearch?.dates?.endDate, landingSearch?.dates?.startDate]);

  useEffect(() => {
    const nextGuests = normalizeGuestMix(landingSearch?.guests);
    setGuestMix((prev) => {
      if (prev.adults === nextGuests.adults && prev.children === nextGuests.children && prev.infants === nextGuests.infants) return prev;
      return nextGuests;
    });
  }, [landingSearch?.guests]);

  useEffect(() => {
    const nextLocation = landingSearch?.location || {};
    setLocationSelection((prev) => {
      const next = {
        district: nextLocation.district || "",
        destinationSlug: nextLocation.destinationSlug || "",
        destinationLabel: nextLocation.destinationLabel || "",
        location: nextLocation.location || "",
        campSlug: nextLocation.campSlug || "",
        experienceType: nextLocation.experienceType || "",
        experienceSlug: nextLocation.experienceSlug || "",
        experiencePath: nextLocation.experiencePath || "",
        experienceCategory: nextLocation.experienceCategory || "",
      };
      if (
        prev.district === next.district &&
        prev.destinationSlug === next.destinationSlug &&
        prev.destinationLabel === next.destinationLabel &&
        prev.location === next.location &&
        prev.campSlug === next.campSlug &&
        prev.experienceType === next.experienceType &&
        prev.experienceSlug === next.experienceSlug &&
        prev.experiencePath === next.experiencePath &&
        prev.experienceCategory === next.experienceCategory
      )
        return prev;
      return next;
    });
  }, [
    landingSearch?.location?.district,
    landingSearch?.location?.destinationSlug,
    landingSearch?.location?.destinationLabel,
    landingSearch?.location?.location,
    landingSearch?.location?.campSlug,
    landingSearch?.location?.experienceType,
    landingSearch?.location?.experienceSlug,
    landingSearch?.location?.experiencePath,
    landingSearch?.location?.experienceCategory,
  ]);

  useEffect(() => {
    const nextOrigin = landingSearch?.itinerary?.origin || "";
    const nextDestination = landingSearch?.itinerary?.destination || "";
    setItineraryInputs((prev) => {
      if (prev.origin === nextOrigin && prev.destination === nextDestination) return prev;
      return { origin: nextOrigin, destination: nextDestination };
    });
  }, [landingSearch?.itinerary?.destination, landingSearch?.itinerary?.origin]);

  // Sticky
  useEffect(() => {
    if (!allowSticky) {
      if (isSticky) setIsSticky(false);
      return;
    }

    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    let frameId = 0;

    const computeStickyState = () => {
      if (window.scrollY === 0) {
        setIsSticky(false);
        return;
      }
      const isHomeRoute = location.pathname === "/";
      if (!isHomeRoute) {
        setIsSticky(window.scrollY > STICKY_SCROLL_OFFSET);
        return;
      }

      const bannerNode = document.getElementById(HOME_PROMOTION_BANNER_ID);
      const bannerHeight = bannerNode?.getBoundingClientRect?.().height || 0;
      if (!bannerNode || bannerHeight <= 1) {
        setIsSticky(window.scrollY > STICKY_SCROLL_OFFSET);
        return;
      }

      const stickyHeight = stickyWrapperRef.current?.getBoundingClientRect?.().height || 0;
      const bannerBottom = bannerNode.getBoundingClientRect().bottom;
      setIsSticky(bannerBottom <= Math.max(stickyHeight, 0));
    };

    const onScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        computeStickyState();
      });
    };

    computeStickyState();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    let observer = null;
    const bannerNode = document.getElementById(HOME_PROMOTION_BANNER_ID);
    if (typeof ResizeObserver !== "undefined" && bannerNode) {
      observer = new ResizeObserver(() => onScroll());
      observer.observe(bannerNode);
    }

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      observer?.disconnect?.();
    };
  }, [allowSticky, isSticky, location.pathname]);

  useEffect(() => {
    if (!isMobile && !isSearchWizardOpen) return;
    if (!isExperienceOpen && !isLocationOpen && !isDateOpen) return;
    setIsExperienceOpen(false);
    setIsLocationOpen(false);
    setIsDateOpen(false);
  }, [isDateOpen, isExperienceOpen, isLocationOpen, isMobile, isSearchWizardOpen]);

  // Outside click / esc for mega menu
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) setIsMenuOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  // Experience popover outside click / esc
  useEffect(() => {
    if (!isExperienceOpen) return;

    const handleKeyDown = (event) => event.key === "Escape" && setIsExperienceOpen(false);

    const handleClick = (event) => {
      const triggerEl = experienceTriggerRef.current;
      const popoverEl = experiencePopoverRef.current;
      if (triggerEl?.contains(event.target) || popoverEl?.contains(event.target)) return;
      setIsExperienceOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [isExperienceOpen]);

  // Location popover outside click / esc
  useEffect(() => {
    if (!isLocationOpen) return;

    const handleKeyDown = (event) => event.key === "Escape" && setIsLocationOpen(false);

    const handleClick = (event) => {
      const triggerEl = locationTriggerRef.current;
      const popoverEl = locationPopoverRef.current;
      if (triggerEl?.contains(event.target) || popoverEl?.contains(event.target)) return;
      setIsLocationOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [isLocationOpen]);

  // Date popover outside click / esc
  useEffect(() => {
    if (!isDateOpen) return;

    const handleKeyDown = (event) => event.key === "Escape" && setIsDateOpen(false);

    const handleClick = (event) => {
      const triggerEl = dateTriggerRef.current;
      const popoverEl = datePopoverRef.current;
      if (triggerEl?.contains(event.target) || popoverEl?.contains(event.target)) return;
      setIsDateOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [isDateOpen]);

  // mobile scroll lock (single source of truth)
  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    const shouldLock =
      window.innerWidth < 768 &&
      (isExperienceOpen ||
        isLocationOpen ||
        isDateOpen ||
        isMenuOpen ||
        isMobileMenuOpen ||
        isSideMenuOpen ||
        isSearchWizardOpen);
    if (!shouldLock) {
      document.body.style.overflow = "";
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [
    isExperienceOpen,
    isLocationOpen,
    isDateOpen,
    isMenuOpen,
    isMobileMenuOpen,
    isSideMenuOpen,
    isSearchWizardOpen,
  ]);

  useEffect(() => {
    if (isSearchWizardOpen) {
      lastWizardOpenRef.current = true;
      return;
    }
    if (lastWizardOpenRef.current && !wizardCompletedRef.current) {
      trackEvent("search_wizard_dropoff", {
        mode: searchMode,
        sheet: mobileSheet || "main",
      });
    }
    lastWizardOpenRef.current = false;
  }, [isSearchWizardOpen, mobileSheet, searchMode, trackEvent]);

  useEffect(() => {
    if (!isSearchWizardOpen) return;
    const dialog = wizardRef.current;
    if (!dialog) return;
    const previouslyFocused = document.activeElement;
    const focusable = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0] || dialog;
    const last = focusable[focusable.length - 1] || dialog;
    if (typeof first.focus === "function") first.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsSearchWizardOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [isSearchWizardOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleAuthChange = () => setSession(getActiveSession());
    handleAuthChange();
    window.addEventListener(AUTH_EVENT, handleAuthChange);
    return () => window.removeEventListener(AUTH_EVENT, handleAuthChange);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOpenAuth = (event) => {
      if (event?.detail?.source === "checkout") return;
      requestPortalAuth(event?.detail || {});
    };
    window.addEventListener("portal-auth:open", handleOpenAuth);
    return () => window.removeEventListener("portal-auth:open", handleOpenAuth);
  }, [requestPortalAuth]);

  useEffect(() => {
    if (isMenuOpen) return;
    if (mobileMenuSection) setMobileMenuSection("");
  }, [isMenuOpen, mobileMenuSection]);

  useEffect(() => {
    if (!isMenuOpen) return;
    if (!menuPrimaryItems.some((item) => item.key === menuPrimary)) {
      setMenuPrimary("destinations");
    }
  }, [isMenuOpen, menuPrimary, menuPrimaryItems]);

  useEffect(() => {
    if (!isMenuOpen) return;
    if (!["destinations", "stay-options"].includes(menuPrimary)) return;
    if (!destinationItems.length) return;
    const firstSlug = normalizeDestinationSlug(destinationItems[0]);
    const hasMatch = destinationItems.some(
      (item) => normalizeDestinationSlug(item) === menuDestinationSlug,
    );
    if (!hasMatch && firstSlug) {
      setMenuDestinationSlug(firstSlug);
    }
  }, [destinationItems, isMenuOpen, menuDestinationSlug, menuPrimary]);

  useEffect(() => {
    if (!isMenuOpen || menuPrimary !== "experiences") return;
    if (!menuExperienceType) setMenuExperienceType("safari");
  }, [isMenuOpen, menuExperienceType, menuPrimary]);

  const experienceMap = useMemo(() => {
    if (menuExperienceType === "safari") return safarisByDestination;
    if (menuExperienceType === "cruise") return cruisesByDestination;
    return experiencesByDestination;
  }, [cruisesByDestination, experiencesByDestination, menuExperienceType, safarisByDestination]);

  const resolveDistrictSlug = useCallback((entry) => {
    const raw = String(entry?.slug || "").trim();
    if (raw) return canonicalizeDistrictSlug(raw);
    const name = String(entry?.district || entry?.label || "").trim();
    return name ? canonicalizeDistrictSlug(name) : "";
  }, []);

  const stayGroups = useMemo(() => {
    const groups = [];
    campsByDestination.forEach((camps, destinationSlug) => {
      const sorted = Array.isArray(camps)
        ? [...camps].sort((a, b) =>
            String(a?.name || a?.label || a?.location || "").localeCompare(
              String(b?.name || b?.label || b?.location || ""),
            ),
          )
        : [];
      if (!sorted.length) return;
      groups.push({
        destinationSlug,
        destinationLabel: resolveDestinationLabel(destinationSlug),
        camps: sorted,
      });
    });
    return groups.sort((a, b) =>
      String(a.destinationLabel || "").localeCompare(String(b.destinationLabel || "")),
    );
  }, [campsByDestination, resolveDestinationLabel]);

  const experienceGroups = useMemo(() => {
    if (!experienceMap || !experienceMap.size) return [];
    const groups = [];
    experienceMap.forEach((entries, destinationSlug) => {
      const sorted = Array.isArray(entries)
        ? [...entries].sort((a, b) =>
            String(a?.label || a?.title || a?.name || "").localeCompare(
              String(b?.label || b?.title || b?.name || ""),
            ),
          )
        : [];
      if (!sorted.length) return;
      groups.push({
        destinationSlug,
        destinationLabel: resolveDestinationLabel(destinationSlug),
        entries: sorted,
      });
    });
    return groups.sort((a, b) =>
      String(a.destinationLabel || "").localeCompare(String(b.destinationLabel || "")),
    );
  }, [experienceMap, resolveDestinationLabel]);

  const menuDetailSlug = useMemo(() => {
    if (!isMenuOpen) return "";
    return menuDestinationSlug;
  }, [isMenuOpen, menuDestinationSlug]);

  useEffect(() => {
    if (!isMenuOpen || !menuDetailSlug) return;
    const existing = menuDestinationDetailState[menuDetailSlug];
    if (existing?.status === "loading" || existing?.status === "ready") return;
    setMenuDestinationDetailState((prev) => ({
      ...prev,
      [menuDetailSlug]: { status: "loading", destination: null, error: null },
    }));
    fetchDestinationBySlug(menuDetailSlug)
      .then((response) => {
        const destination = response?.destination || response?.data?.destination || null;
        setMenuDestinationDetailState((prev) => ({
          ...prev,
          [menuDetailSlug]: { status: "ready", destination, error: null },
        }));
      })
      .catch((error) => {
        setMenuDestinationDetailState((prev) => ({
          ...prev,
          [menuDetailSlug]: {
            status: "error",
            destination: null,
            error: error?.message || "Unable to load destination details",
          },
        }));
      });
  }, [isMenuOpen, menuDetailSlug, menuDestinationDetailState]);

  useEffect(() => {
    if (!isMenuOpen || !menuDetailSlug) return;
    const existing = menuActivityCountState[menuDetailSlug];
    if (existing?.status === "loading" || existing?.status === "ready") return;
    setMenuActivityCountState((prev) => ({
      ...prev,
      [menuDetailSlug]: { status: "loading", safari: 0, cruise: 0, trek: 0 },
    }));
    Promise.allSettled([
      fetchSafariFeed({ destinationSlug: menuDetailSlug, limit: 1, includeTotal: true }),
      fetchCruiseFeed({ destinationSlug: menuDetailSlug, limit: 1, includeTotal: true }),
      fetchTrekFeed({ destinationSlug: menuDetailSlug, limit: 1, includeTotal: true }),
    ]).then((results) => {
      const safariCount =
        results[0].status === "fulfilled" ? resolveFeedCount(results[0].value) : 0;
      const cruiseCount =
        results[1].status === "fulfilled" ? resolveFeedCount(results[1].value) : 0;
      const trekCount =
        results[2].status === "fulfilled" ? resolveFeedCount(results[2].value) : 0;
      setMenuActivityCountState((prev) => ({
        ...prev,
        [menuDetailSlug]: {
          status: "ready",
          safari: safariCount,
          cruise: cruiseCount,
          trek: trekCount,
        },
      }));
    });
  }, [isMenuOpen, menuActivityCountState, menuDetailSlug]);

  const requestCampInventory = useCallback((destinationSlug) => {
    if (!destinationSlug) return;
    setCampInventoryState((prev) => ({
      ...prev,
      [destinationSlug]: { status: "loading", camps: [], error: null },
    }));
    fetchDestinationCamps(destinationSlug, { includeRooms: true })
      .then((response) => {
        const camps = Array.isArray(response?.camps) ? response.camps : [];
        setCampInventoryState((prev) => ({
          ...prev,
          [destinationSlug]: { status: "ready", camps, error: null },
        }));
      })
      .catch((error) => {
        setCampInventoryState((prev) => ({
          ...prev,
          [destinationSlug]: {
            status: "error",
            camps: [],
            error: error?.message || "Unable to load camp inventory",
          },
        }));
      });
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const destinationSlug = menuPrimary === "destinations" ? menuDestinationSlug : "";
    if (!destinationSlug) return;
    const campState = campInventoryState[destinationSlug];
    if (campState?.status === "loading" || campState?.status === "ready") return;
    requestCampInventory(destinationSlug);
  }, [campInventoryState, isMenuOpen, menuDestinationSlug, menuPrimary, requestCampInventory]);

  // ======= dropdown =======

  const renderMenuDropdown = () => {
    if (!isMenuOpen) return null;
    const handleMenuClose = () => setIsMenuOpen(false);
    const handleMobileSectionToggle = (key) => {
      setMenuPrimary(key);
      setMobileMenuSection((prev) => (prev === key ? "" : key));
    };
    const buildExperienceLink = (entry, destinationSlug) => {
      const slug = resolveExperienceSlug(entry);
      const activeDestinationSlug = destinationSlug;
      if (!activeDestinationSlug) return EXPERIENCE_LINKS[menuExperienceType] || "/experiences";
      if (menuExperienceType === "safari") {
        return slug
          ? `${EXPERIENCE_LINKS.safari}/${activeDestinationSlug}/${slug}`
          : `/destination/${activeDestinationSlug}?section=${EXPERIENCE_SECTION_ID}&xpTab=safari`;
      }
      if (menuExperienceType === "cruise") {
        return slug
          ? `${EXPERIENCE_LINKS.cruise}/${activeDestinationSlug}/${slug}`
          : `/destination/${activeDestinationSlug}?section=${EXPERIENCE_SECTION_ID}&xpTab=cruise`;
      }
      if (slug) {
        return `/treks-trails/${activeDestinationSlug}/${slug}`;
      }
      return `/destination/${activeDestinationSlug}?section=${EXPERIENCE_SECTION_ID}&xpTab=trek`;
    };

    if (isMobile) {
      const buildDestinationLink = (slug) => (slug ? `/destination/${slug}` : "/stays-camps");
      const experienceBrowseLink =
        menuExperienceType === "safari"
          ? EXPERIENCE_LINKS.safari
          : menuExperienceType === "cruise"
          ? EXPERIENCE_LINKS.cruise
          : "";
      const activeExperienceLabel =
        EXPERIENCE_TYPES.find((type) => type.key === menuExperienceType)?.label ||
        menuExperienceType;
      const normalizedExperienceBrowseLink = experienceBrowseLink
        ? experienceBrowseLink.startsWith("/")
          ? experienceBrowseLink
          : `/${experienceBrowseLink}`
        : "";
      const mobileSections = menuPrimaryItems.map((item) => ({
        key: item.key,
        label: item.label,
        icon: item.icon,
      }));
      const renderMobileSectionBody = (key) => {
        if (key === "destinations") {
          return (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={UI.menuSubtle}>Browse</span>
                <Link
                  to="/stays-camps"
                  onClick={handleMenuClose}
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-900"
                >
                  Browse all
                </Link>
              </div>
              {destinationItems.length ? (
                <div className="space-y-1">
                  {destinationItems.map((entry) => {
                    const slug = normalizeDestinationSlug(entry);
                    if (!slug) return null;
                    return (
                      <Link
                        key={slug}
                        to={buildDestinationLink(slug)}
                        onClick={handleMenuClose}
                        className={UI.menuItem}
                      >
                        <span className="truncate">{entry.label || entry.name || slug}</span>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                  No {BRAND_LABELS_LOWER.destinations} listed yet.
                </div>
              )}
            </div>
          );
        }

        if (key === "stay-options") {
          return (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={UI.menuSubtle}>Stay options</span>
                <Link
                  to="/stays-camps"
                  onClick={handleMenuClose}
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-900"
                >
                  Browse all
                </Link>
              </div>
              {stayGroups.length ? (
                <div className="space-y-3">
                  {stayGroups.map((group) => (
                    <div key={group.destinationSlug} className="rounded-xl border border-slate-100 bg-white p-2">
                      <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                        {group.destinationLabel}
                      </div>
                      <div className="space-y-1">
                        {group.camps.map((camp) => {
                          const campSlug = resolveCampSlug(camp);
                          if (!campSlug) return null;
                          return (
                            <Link
                              key={`${group.destinationSlug}:${campSlug}`}
                              to={`/stays-camps/${group.destinationSlug}/${campSlug}`}
                              onClick={handleMenuClose}
                              className={UI.menuItem}
                            >
                              <span className="truncate">{camp.name || camp.label || camp.location || campSlug}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                  No {BRAND_LABELS_LOWER.stays} listed yet.
                </div>
              )}
            </div>
          );
        }

        if (key === "experiences") {
          return (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {EXPERIENCE_TYPES.map((type) => {
                  const isActive = menuExperienceType === type.key;
                  return (
                    <button
                      key={type.key}
                      type="button"
                      onClick={() => setMenuExperienceType(type.key)}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition ${
                        isActive
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <i className={`${type.icon} text-[12px]`} aria-hidden="true" />
                      {type.label}
                    </button>
                  );
                })}
              </div>
              {normalizedExperienceBrowseLink ? (
                <Link
                  to={normalizedExperienceBrowseLink}
                  onClick={handleMenuClose}
                  className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-700"
                >
                  Browse all {activeExperienceLabel}
                </Link>
              ) : null}
              {experienceGroups.length ? (
                <div className="space-y-3">
                  {experienceGroups.map((group) => (
                    <div key={group.destinationSlug} className="rounded-xl border border-slate-100 bg-white p-2">
                      <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                        {group.destinationLabel}
                      </div>
                      <div className="space-y-1">
                        {group.entries.map((entry) => (
                          <Link
                            key={`${group.destinationSlug}:${entry.id || entry.slug || entry.label || entry.title}`}
                            to={buildExperienceLink(entry, group.destinationSlug)}
                            onClick={handleMenuClose}
                            className={UI.menuItem}
                          >
                            <span className="truncate">{entry.label || entry.title || entry.name || "Experience"}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                  {menuExperienceType === "cruise"
                    ? "Cruise routes are loading."
                    : "Experience options are loading."}
                </div>
              )}
            </div>
          );
        }

        if (key === "forms") {
          return (
            <div className="space-y-2">
              {activeHostedForms.length ? (
                <div className="space-y-2">
                  {activeHostedForms.map((form) => (
                    <Link
                      key={form.id || form.slug}
                      to={form.publicPath}
                      onClick={handleMenuClose}
                      className="block rounded-xl border border-slate-100 bg-white p-3 transition hover:border-slate-200 hover:bg-slate-50"
                    >
                      <div className="text-[13px] font-semibold text-slate-900">{form.title}</div>
                      {form.summaryPreview ? (
                        <div className="mt-1 text-[12px] leading-5 text-slate-500">{form.summaryPreview}</div>
                      ) : null}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                  No active forms available right now.
                </div>
              )}
            </div>
          );
        }

        if (key === "content") {
          return (
            <div className="space-y-2">
              {activeContentLinks.length ? (
                <div className="space-y-2">
                  {activeContentLinks.map((item) => (
                    <Link
                      key={item.id || item.slug || item.publicPath}
                      to={item.publicPath}
                      onClick={handleMenuClose}
                      className="block rounded-xl border border-slate-100 bg-white p-3 transition hover:border-slate-200 hover:bg-slate-50"
                    >
                      <div className="text-[13px] font-semibold text-slate-900">{item.label}</div>
                      {item.summaryPreview ? (
                        <div className="mt-1 text-[12px] leading-5 text-slate-500">{item.summaryPreview}</div>
                      ) : null}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                  No published content sections are available right now.
                </div>
              )}
            </div>
          );
        }

        if (key === "districts") {
          return (
            <div className="space-y-3">
              {districtItems.length ? (
                <div className="space-y-1">
                  {districtItems.map((district) => {
                    const name = canonicalizeDistrictName(district.district || district.label);
                    const districtSlug = resolveDistrictSlug(district);
                    if (!districtSlug) return null;
                    return (
                      <Link
                        key={districtSlug}
                        to={`/district/${districtSlug}`}
                        onClick={handleMenuClose}
                        className={UI.menuItem}
                      >
                        <span className="truncate">{name}</span>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                  No {BRAND_LABELS_LOWER.districts} available.
                </div>
              )}
            </div>
          );
        }

        if (key === "account") {
          return (
            <div className="space-y-1">
              {ACCOUNT_QUICK_LINKS.map((item) => (
                <Link key={item.label} to={item.path} onClick={handleMenuClose} className={UI.menuItem}>
                  <span className="inline-flex items-center gap-2">
                    <i className={item.icon} aria-hidden="true" />
                    {item.label}
                  </span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                </Link>
              ))}
            </div>
          );
        }

        if (key === "access") {
          return (
            <div className="space-y-1">
              {session ? (
                <button type="button" onClick={handleLogout} className={UI.menuItem}>
                  <span className="inline-flex items-center gap-2">
                    <i className="fa-regular fa-right-from-bracket" aria-hidden="true" />
                    Logout
                  </span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleAuthAction("login")}
                    className={UI.menuItem}
                  >
                    <span className="inline-flex items-center gap-2">
                      <i className="fa-regular fa-user" aria-hidden="true" />
                      Sign in
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAuthAction("signup")}
                    className={UI.menuItem}
                  >
                    <span className="inline-flex items-center gap-2">
                      <i className="fa-regular fa-user-plus" aria-hidden="true" />
                      Sign up
                    </span>
                  </button>
                </>
              )}
            </div>
          );
        }

        return null;
      };

      return (
        <div
          id="global-navigation-menu"
          role="menu"
          aria-label="EcoTour Odisha main navigation"
          className="fixed inset-0 z-[1300] flex h-[100svh] flex-col bg-white"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Menu</div>
            <button
              type="button"
              onClick={handleMenuClose}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
              Close
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
            <div className="space-y-4">
              <div className="space-y-2">
                {mobileSections.map((item) => {
                  const isExpanded = mobileMenuSection === item.key;
                  const panelId = `mobile-menu-panel-${item.key}`;
                  return (
                    <div
                      key={item.key}
                      className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_12px_36px_rgba(15,23,42,0.08)]"
                    >
                      <button
                        type="button"
                        onClick={() => handleMobileSectionToggle(item.key)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                        aria-expanded={isExpanded}
                        aria-controls={panelId}
                      >
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                          <i className={item.icon} aria-hidden="true" />
                          {item.label}
                        </span>
                        <i
                          className={`fa-solid text-[11px] text-slate-400 transition-transform ${
                            isExpanded ? "fa-chevron-up" : "fa-chevron-down"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                      {isExpanded ? (
                        <div id={panelId} className="border-t border-slate-100 p-3">
                          {renderMobileSectionBody(item.key)}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_12px_36px_rgba(15,23,42,0.08)]">
                <div className={UI.menuSubtle}>Information</div>
                <div className="mt-2 space-y-1">
                  {PORTAL_INFO_LINKS.map((item) => (
                    <Link key={item.label} to={item.path} onClick={handleMenuClose} className={UI.menuItem}>
                      <span className="inline-flex items-center gap-2">
                        <i className={item.icon} aria-hidden="true" />
                        {item.label}
                      </span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_12px_36px_rgba(15,23,42,0.08)]">
                <div className={UI.menuSubtle}>Account</div>
                <div className="mt-2 space-y-1">
                  {ACCOUNT_QUICK_LINKS.map((item) => (
                    <Link key={item.label} to={item.path} onClick={handleMenuClose} className={UI.menuItem}>
                      <span className="inline-flex items-center gap-2">
                        <i className={item.icon} aria-hidden="true" />
                        {item.label}
                      </span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_12px_36px_rgba(15,23,42,0.08)]">
                <div className={UI.menuSubtle}>Access</div>
                <div className="mt-2 space-y-1">
                  {session ? (
                    <button
                      type="button"
                      onClick={handleLogout}
                      className={UI.menuItem}
                    >
                      <span className="inline-flex items-center gap-2">
                        <i className="fa-regular fa-right-from-bracket" aria-hidden="true" />
                        Logout
                      </span>
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleAuthAction("login")}
                        className={UI.menuItem}
                      >
                        <span className="inline-flex items-center gap-2">
                          <i className="fa-regular fa-user" aria-hidden="true" />
                          Sign in
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAuthAction("signup")}
                        className={UI.menuItem}
                      >
                        <span className="inline-flex items-center gap-2">
                          <i className="fa-regular fa-user-plus" aria-hidden="true" />
                          Sign up
                        </span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const showNestedPanel = menuPrimaryItems.some((item) => item.key === menuPrimary);
    const activeDestinationSlug = menuDetailSlug;
    const activeDestinationLabel = resolveDestinationLabel(activeDestinationSlug);
    const activeDestinationLink = activeDestinationSlug
      ? `/destination/${activeDestinationSlug}`
      : "/stays-camps";
    const activeStayLink = activeDestinationSlug
      ? `/stays-camps?destinationSlug=${activeDestinationSlug}`
      : "/stays-camps";
    const campInventory = menuPrimary === "destinations" ? campInventoryState[activeDestinationSlug] : null;
    const campInventoryStatus = campInventory?.status;
    const campInventoryCamps = campInventory?.camps || [];
    const fallbackCamps = activeDestinationSlug
      ? campsByDestination.get(activeDestinationSlug) || []
      : [];
    const campsToShow = campInventoryCamps.length ? campInventoryCamps : fallbackCamps;
    const destinationDetail = activeDestinationSlug
      ? menuDestinationDetailState[activeDestinationSlug]?.destination
      : null;
    const nearbyDestinations = Array.isArray(destinationDetail?.nearbyDestinations)
      ? destinationDetail.nearbyDestinations
      : [];
    const destinationActivities = Array.isArray(destinationDetail?.activities)
      ? destinationDetail.activities
      : [];
    const activityCounts = menuActivityCountState[activeDestinationSlug] || {};
    const safariCount =
      Number.isFinite(activityCounts?.safari)
        ? activityCounts.safari
        : (safarisByDestination.get(activeDestinationSlug) || []).length;
    const cruiseCount =
      Number.isFinite(activityCounts?.cruise)
        ? activityCounts.cruise
        : (cruisesByDestination.get(activeDestinationSlug) || []).length;
    const trekCount =
      Number.isFinite(activityCounts?.trek)
        ? activityCounts.trek
        : (experiencesByDestination.get(activeDestinationSlug) || []).length;
    const stayOptionsCount = campInventoryCamps.length || fallbackCamps.length;

    const activitySummary = [
      {
        label: `${stayOptionsCount} stay option${stayOptionsCount === 1 ? "" : "s"}`,
        count: stayOptionsCount,
        href: activeStayLink,
      },
      {
        label: `${safariCount} Safari option${safariCount === 1 ? "" : "s"}`,
        count: safariCount,
        href: `${activeDestinationLink}?section=${EXPERIENCE_SECTION_ID}&xpTab=safari`,
      },
      {
        label: `${cruiseCount} Cruise & lake ride${cruiseCount === 1 ? "" : "s"}`,
        count: cruiseCount,
        href: `${activeDestinationLink}?section=${EXPERIENCE_SECTION_ID}&xpTab=cruise`,
      },
      {
        label: `${trekCount} Trek route${trekCount === 1 ? "" : "s"}`,
        count: trekCount,
        href: `${activeDestinationLink}?section=${EXPERIENCE_SECTION_ID}&xpTab=trek`,
      },
    ].filter((entry) => entry.count);

    const activityExtras = destinationActivities
      .map((activity) => activity?.name || activity?.title || activity?.label)
      .filter(Boolean)
      .slice(0, 6);
    const attachedDestinations =
      nearbyDestinations.length
        ? nearbyDestinations
            .map((entry) => {
              const label = entry?.name || entry?.title || "";
              const id = entry?.id || "";
              const slug = id || slugify(label);
              return label && slug ? { label, slug } : null;
            })
            .filter(Boolean)
            .slice(0, 6)
        : (() => {
            const baseDestination = destinationLookup?.get?.(activeDestinationSlug);
            const district = baseDestination?.district;
            if (!district) return [];
            return (destinationsByDistrict.get(district) || [])
              .filter((entry) => normalizeDestinationSlug(entry) !== activeDestinationSlug)
              .slice(0, 6)
              .map((entry) => ({
                label: entry.label,
                slug: normalizeDestinationSlug(entry),
              }));
          })();

    const renderCampCard = (camp, { showInventory } = {}) => {
      if (!camp) return null;
      const campName = camp.name || camp.label || camp.location || "Camp";
      const campSlug = resolveCampSlug(camp);
      const destinationSlug = camp.destinationSlug || activeDestinationSlug;
      const campLink =
        destinationSlug && campSlug
          ? `/stays-camps/${destinationSlug}/${campSlug}`
          : activeDestinationLink;
      const rooms = Array.isArray(camp.rooms) ? camp.rooms : [];
      const roomPreview = rooms.slice(0, 3);
      return (
        <div key={camp.id || camp.slug || campName} className="rounded-xl border border-slate-100 bg-white p-3">
          <Link to={campLink} onClick={handleMenuClose} className="text-[13px] font-semibold text-slate-900 hover:text-sky-700">
            {campName}
          </Link>
          {showInventory ? (
            rooms.length ? (
              <ul className="mt-2 space-y-1 text-[12px] text-slate-600">
                {roomPreview.map((room) => {
                  const inventory =
                    Number.isFinite(room?.inventory) ? `${room.inventory} rooms` : "Limited inventory";
                  return (
                    <li key={room.id || room.slug || room.name} className="flex items-center justify-between gap-2">
                      <span className="truncate">{room.name || room.slug}</span>
                      <span className="text-[11px] font-semibold text-slate-500">{inventory}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="mt-2 text-[11px] text-slate-500">Inventory is being updated.</div>
            )
          ) : (
            <div className="mt-2 text-[11px] text-slate-500">View Details on camp page.</div>
          )}
        </div>
      );
    };

    return (
      <div
        id="global-navigation-menu"
        role="menu"
        aria-label="EcoTour Odisha main navigation"
        className={`${UI.menuPopover} w-[min(260px,92vw)]`}
      >
        <div className="relative w-[min(260px,92vw)] p-2">
          <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Explore
          </div>
          <div className="space-y-1">
            {menuPrimaryItems.map((item) => {
              const isActive = menuPrimary === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onMouseEnter={() => setMenuPrimary(item.key)}
                  onClick={() => setMenuPrimary(item.key)}
                  className={`${UI.menuItem} ${isActive ? UI.menuItemActive : ""}`}
                >
                  <i className="fa-solid fa-chevron-left text-[10px] mr-auto" aria-hidden="true" />
                  <span className="inline-flex items-center gap-2">
                    <i className={item.icon} aria-hidden="true" />
                    {item.label}
                  </span>
                </button>
              );
            })}
            {PORTAL_INFO_LINKS.map((item) => (
              <Link key={item.label} to={item.path} onClick={handleMenuClose} className={UI.menuItem}>
                <span className="inline-flex items-center gap-2">
                  <i className={item.icon} aria-hidden="true" />
                  {item.label}
                </span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <div className={UI.menuDivider} />
          <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Account
          </div>
          <div className="space-y-1">
            {ACCOUNT_QUICK_LINKS.map((item) => (
              <Link key={item.label} to={item.path} onClick={handleMenuClose} className={UI.menuItem}>
                <span className="inline-flex items-center gap-2">
                  <i className={item.icon} aria-hidden="true" />
                  {item.label}
                </span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </Link>
            ))}
          </div>

          <div className={UI.menuDivider} />
          <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Access
          </div>
          <div className="space-y-1">
            {session ? (
              <button type="button" onClick={handleLogout} className={UI.menuItem}>
                <span className="inline-flex items-center gap-2">
                  <i className="fa-regular fa-right-from-bracket" aria-hidden="true" />
                  Logout
                </span>
              </button>
            ) : (
              <>
                <button type="button" onClick={() => handleAuthAction("login")} className={UI.menuItem}>
                  <span className="inline-flex items-center gap-2">
                    <i className="fa-regular fa-user" aria-hidden="true" />
                    Sign in
                  </span>
                </button>
                <button type="button" onClick={() => handleAuthAction("signup")} className={UI.menuItem}>
                  <span className="inline-flex items-center gap-2">
                    <i className="fa-regular fa-user-plus" aria-hidden="true" />
                    Sign up
                  </span>
                </button>
              </>
            )}
          </div>

          {showNestedPanel ? (
            <div className={`absolute right-full top-0 mr-3 w-[min(720px,calc(100vw-320px))] ${UI.menuPanel} pointer-events-auto`}>
              {menuPrimary === "destinations" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      {BRAND_LABELS.destinations}
                    </div>
                    <Link to="/stays-camps" onClick={handleMenuClose} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">
                      Browse all
                    </Link>
                  </div>
                  <div className="grid gap-3 lg:grid-cols-[1fr_220px]">
                    <div className="max-h-[420px] overflow-auto pr-1">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-[13px] font-semibold text-slate-900">
                          {activeDestinationLabel ||
                            `Select a ${BRAND_LABELS_LOWER.destinations}`}
                        </div>
                        {activeDestinationSlug ? (
                          <Link to={activeDestinationLink} onClick={handleMenuClose} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">
                            View {BRAND_LABELS_LOWER.destinations}
                          </Link>
                        ) : null}
                      </div>
                      {campInventoryStatus === "loading" ? (
                        <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                          Loading camp inventories…
                        </div>
                      ) : null}
                      {campInventoryStatus === "error" ? (
                        <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-3 text-[12px] text-rose-700">
                          Unable to load inventories right now.
                        </div>
                      ) : null}
                      <div className="space-y-3">
                        {campsToShow.length ? (
                          campsToShow.map((camp) => renderCampCard(camp, { showInventory: true }))
                        ) : (
                          <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                            No camps available for this {BRAND_LABELS_LOWER.destinations}.
                          </div>
                        )}
                      </div>
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div className="rounded-xl border border-slate-100 bg-white p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                            Activities
                          </div>
                          <ul className="mt-2 space-y-1 text-[12px] text-slate-600">
                            {activitySummary.length ? (
                              activitySummary.map((entry) => (
                                <li key={entry.label}>
                                  <Link to={entry.href} onClick={handleMenuClose} className="hover:text-slate-900">
                                    {entry.label}
                                  </Link>
                                </li>
                              ))
                            ) : (
                              <li>Activities are being updated.</li>
                            )}
                            {activityExtras.map((label) => (
                              <li key={label}>
                                <Link
                                  to={`${activeDestinationLink}?section=overview-section-signature-activities`}
                                  onClick={handleMenuClose}
                                  className="hover:text-slate-900"
                                >
                                  {label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-white p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                            Attached {BRAND_LABELS_LOWER.destinations}
                          </div>
                          <ul className="mt-2 space-y-1 text-[12px] text-slate-600">
                            {attachedDestinations.length ? (
                              attachedDestinations.map((entry) => (
                                <li key={entry.slug}>
                                  <Link
                                    to={`/destination/${entry.slug}`}
                                    onClick={handleMenuClose}
                                    className="hover:text-slate-900"
                                  >
                                    {entry.label}
                                  </Link>
                                </li>
                              ))
                            ) : (
                              <li>Nearby {BRAND_LABELS_LOWER.destinations} are being updated.</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="max-h-[420px] overflow-auto pr-1">
                      {destinationItems.map((item) => {
                        const slug = normalizeDestinationSlug(item);
                        const isActive = slug === menuDestinationSlug;
                        return (
                          <button
                            key={slug}
                            type="button"
                            onClick={() => setMenuDestinationSlug(slug)}
                            className={`${UI.menuItem} ${isActive ? UI.menuItemActive : ""}`}
                          >
                            <span className="truncate">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {menuPrimary === "stay-options" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      {BRAND_LABELS.stays}
                    </div>
                    <Link to="/stays-camps" onClick={handleMenuClose} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">
                      Browse {BRAND_LABELS_LOWER.stays}
                    </Link>
                  </div>
                  <div className="max-h-[460px] overflow-auto pr-1 space-y-3">
                    {stayGroups.length ? (
                      stayGroups.map((group) => (
                        <div key={group.destinationSlug} className="rounded-xl border border-slate-100 bg-white p-3">
                          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                            {group.destinationLabel}
                          </div>
                          <div className="space-y-1">
                            {group.camps.map((camp) => {
                              const campSlug = resolveCampSlug(camp);
                              if (!campSlug) return null;
                              return (
                                <Link
                                  key={`${group.destinationSlug}:${campSlug}`}
                                  to={`/stays-camps/${group.destinationSlug}/${campSlug}`}
                                  onClick={handleMenuClose}
                                  className={UI.menuItem}
                                >
                                  <span className="truncate">{camp.name || camp.label || camp.location || campSlug}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                        No camps listed yet.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {menuPrimary === "experiences" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">Experiences</div>
                    {EXPERIENCE_LINKS[menuExperienceType] ? (
                      <Link to={EXPERIENCE_LINKS[menuExperienceType]} onClick={handleMenuClose} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">
                        Browse all
                      </Link>
                    ) : null}
                  </div>
                  <div className="grid gap-3 lg:grid-cols-[1fr_180px]">
                    <div className="max-h-[460px] overflow-auto pr-1 space-y-3">
                      {experienceGroups.length ? (
                        experienceGroups.map((group) => (
                          <div key={group.destinationSlug} className="rounded-xl border border-slate-100 bg-white p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                              {group.destinationLabel}
                            </div>
                            <div className="space-y-1">
                              {group.entries.map((entry) => (
                                <Link
                                  key={`${group.destinationSlug}:${entry.id || entry.slug || entry.label || entry.title}`}
                                  to={buildExperienceLink(entry, group.destinationSlug)}
                                  onClick={handleMenuClose}
                                  className={UI.menuItem}
                                >
                                  <span className="truncate">{entry.label || entry.title || entry.name || "Experience"}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                          No experiences listed yet.
                        </div>
                      )}
                    </div>
                    <div className="max-h-[420px] overflow-auto pr-1">
                      {EXPERIENCE_TYPES.map((type) => {
                        const isActive = menuExperienceType === type.key;
                        return (
                          <button
                            key={type.key}
                            type="button"
                            onClick={() => setMenuExperienceType(type.key)}
                            className={`${UI.menuItem} ${isActive ? UI.menuItemActive : ""}`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <i className={type.icon} aria-hidden="true" />
                              {type.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {menuPrimary === "forms" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">Forms</div>
                  </div>
                  <div className="max-h-[460px] overflow-auto pr-1 space-y-3">
                    {activeHostedForms.length ? (
                      activeHostedForms.map((form) => (
                        <Link
                          key={form.id || form.slug}
                          to={form.publicPath}
                          onClick={handleMenuClose}
                          className="block rounded-xl border border-slate-100 bg-white p-3 transition hover:border-slate-200 hover:bg-slate-50"
                        >
                          <div className="text-[13px] font-semibold text-slate-900">{form.title}</div>
                          {form.summaryPreview ? (
                            <div className="mt-1 text-[12px] leading-5 text-slate-500">{form.summaryPreview}</div>
                          ) : null}
                        </Link>
                      ))
                    ) : (
                      <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                        No active forms available right now.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {menuPrimary === "content" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">{contentMenuLabel}</div>
                  </div>
                  <div className="max-h-[460px] overflow-auto pr-1 space-y-3">
                    {activeContentLinks.length ? (
                      activeContentLinks.map((item) => (
                        <Link
                          key={item.id || item.slug || item.publicPath}
                          to={item.publicPath}
                          onClick={handleMenuClose}
                          className="block rounded-xl border border-slate-100 bg-white p-3 transition hover:border-slate-200 hover:bg-slate-50"
                        >
                          <div className="text-[13px] font-semibold text-slate-900">{item.label}</div>
                          {item.summaryPreview ? (
                            <div className="mt-1 text-[12px] leading-5 text-slate-500">{item.summaryPreview}</div>
                          ) : null}
                        </Link>
                      ))
                    ) : (
                      <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                        No published content sections are available right now.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {menuPrimary === "packages" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">Packages</div>
                    <Link to="/packages" onClick={handleMenuClose} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">
                      Browse all
                    </Link>
                  </div>
                  <div className="px-1.5 py-3">
                    <p className="text-sm text-slate-600">Curated multi-day itineraries combining stays, safaris, cruises and treks.</p>
                    <Link
                      to="/packages"
                      onClick={handleMenuClose}
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#0f5d73] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#0b4a5c]"
                    >
                      Explore packages
                      <i className="fa-solid fa-arrow-right text-[10px]" />
                    </Link>
                  </div>
                </div>
              ) : null}

              {menuPrimary === "districts" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      {BRAND_LABELS.districts}
                    </div>
                    <Link to="/districts" onClick={handleMenuClose} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">
                      View map
                    </Link>
                  </div>
                  <div className="max-h-[460px] overflow-auto pr-1 space-y-1">
                    {districtItems.length ? (
                      districtItems.map((district) => {
                        const name = canonicalizeDistrictName(district.district || district.label);
                        const districtSlug = resolveDistrictSlug(district);
                        if (!districtSlug) return null;
                        return (
                          <Link
                            key={districtSlug}
                            to={`/district/${districtSlug}`}
                            onClick={handleMenuClose}
                            className={UI.menuItem}
                          >
                            <span className="truncate">{name}</span>
                          </Link>
                        );
                      })
                    ) : (
                      <div className="rounded-xl border border-slate-100 bg-white p-3 text-[12px] text-slate-500">
                        No {BRAND_LABELS_LOWER.districts} available.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // ======= render =======

  return (
    <>
      <header className={headerClassName}>
        <div className={stickyWrapperClassName} ref={stickyWrapperRef}>
          <div className={menuAreaClassName} style={{ overflow: "visible" }}>
            <div className={UI.landingWrap}>
              <div className={UI.container} ref={menuRef}>
                {/* LANDING MODE */}
                <div
                  className={landingNavClassName}
                  aria-hidden={showLandingNav ? undefined : "true"}
                  inert={showLandingNav ? undefined : ""}
                >
                  <div className="ecotour-landing-header__nav-row flex items-center justify-between gap-2 px-1.5 py-0.5 md:px-2">
                    <div className={`ecotour-header-brand flex min-w-0 flex-shrink items-center gap-2 sm:flex-shrink-0 ${isSticky ? "h-18" : "h-[66px]"}`}>
                      <Link to="/" className="inline-flex min-w-0 items-center">
                        <PortalBrandLogo
                          alt="EcoTour Odisha"
                          width="150"
                          height="64"
                          className="ecotour-header-brand-logo block h-full w-auto max-w-[108px] object-contain sm:max-w-none max-h-[120px]"
                          decoding="async"
                        />
                      </Link>
                      {showTestPortalBadge ? <PortalTestNavBadge /> : null}
                    </div>

                    <div className="ecotour-landing-header__mode-nav hidden min-w-0 flex-1 justify-center px-2 md:flex">
                      <LandingModeNav value={activeLandingMode} onChange={handleModeChange} />
                    </div>

                    <div className="flex flex-shrink-0 items-center gap-1.5">
                      <ThemeToggle />
                      <button
                        ref={menuButtonRef}
                        type="button"
                        className={`${UI.pill} px-0 py-0`}
                        onClick={() => setIsMenuOpen((prev) => !prev)}
                        aria-expanded={isMenuOpen}
                        aria-haspopup="true"
                        aria-controls="global-navigation-menu"
                        aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                      >
                        <span className={UI.iconPillBlue}>
                          <i className="fa-solid fa-layer-group" aria-hidden="true" />
                        </span>
                        <span className="hidden xs:inline pr-2 text-[12px]">Menu</span>
                      </button>

                      {(isSearchViewportCompact || hydrating) ? (
                        <button
                          type="button"
                          className={`${UI.iconBtn} min-[1100px]:hidden`}
                          onClick={handleHeaderSearchIconClick}
                          aria-label="Open search"
                        >
                          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                        </button>
                      ) : null}

                      <button
                        type="button"
                        className={`hidden lg:inline-flex ${UI.iconBtn}`}
                        onClick={() => setIsSideMenuOpen(true)}
                        aria-label="Open quick drawer"
                      >
                        <i className="fa-solid fa-user" aria-hidden="true" />
                      </button>

                      <button
                        type="button"
                        className={`lg:hidden ${UI.iconBtn}`}
                        onClick={() => setIsSideMenuOpen(true)}
                        aria-label="Open navigation"
                      >
                        <i className="fa-solid fa-user" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  {showLandingNav ? (
                    <div className="ecotour-landing-header__mobile-mode-row px-1.5 pb-0.5 pt-0.5 md:hidden">
                      <LandingModeNav value={activeLandingMode} onChange={handleModeChange} compact />
                    </div>
                  ) : null}

                  {showLandingSearch && (isMobile || hydrating) ? (
                    <div className="ecotour-landing-header__search-row ecotour-landing-header__search-row--mobile md:hidden mt-1 px-2 pb-1.5">
                      <div className="ecotour-landing-header__search-layout">
                        <BookingBar
                          variant="compact"
                          show
                          mode={searchMode}
                          mobileFriendlyCompact
                          showModeToggle={false}
                          experienceCategory={hydrating ? bookingCategories[0] || null : selectedExperienceCategory}
                          experienceSummary={hydrating ? bookingCategories[0]?.label || "Category" : experienceSummary}
                          locationSummary={hydrating ? "Where to?" : locationSummary}
                          dateSummary={hydrating ? formatDateRangeLabel(null, null, searchMode === "experiences") : dateSummary}
                          hasDateSelection={!hydrating && Boolean(dateRange.startDate)}
                          searchButtonLabel={hydrating && searchMode === "experiences" ? `Search ${bookingCategories[0]?.label || "experiences"}` : searchButtonLabel}
                          hasLocationSelection={!hydrating && hasLocationSelection}
                          guestMix={hydrating ? DEFAULT_GUESTS : guestMix}
                          experiencePopoverId={experiencePopoverIdCompact}
                          locationPopoverId={locationPopoverIdCompact}
                          datePopoverId={datePopoverIdCompact}
                          originInput={itineraryInputs.origin}
                          destinationInput={itineraryInputs.destination}
                          onOriginInputChange={handleItineraryOriginChange}
                          onDestinationInputChange={handleItineraryDestinationChange}
                          isExperienceOpen={false}
                          isLocationOpen={false}
                          isDateOpen={false}
                          experienceTriggerRef={experienceTriggerRef}
                          locationTriggerRef={locationTriggerRef}
                          dateTriggerRef={dateTriggerRef}
                          locationPopoverRef={locationPopoverRef}
                          datePopoverRef={datePopoverRef}
                          onModeChange={handleModeChange}
                          onToggleExperience={() => openSearchWizard("category")}
                          onToggleLocation={() => openSearchWizard("location")}
                          onToggleDate={() => {
                            setMobileDraftDates(coerceDateRange(dateRange));
                            openSearchWizard("date");
                          }}
                          onClearLocation={handleLocationClear}
                          onGuestChange={handleGuestChange}
                          onToggleGuests={() => openSearchWizard("guests")}
                          onSearch={() => openSearchWizard("main")}
                          experiencePopover={null}
                          locationPopover={null}
                          datePopover={null}
                        />
                        <ContinueBookingBar isMobile />
                      </div>
                    </div>
                  ) : null}

                  {showLandingSearch && (!isMobile || hydrating) ? (
                    <div className="ecotour-landing-header__search-row ecotour-landing-header__search-row--desktop hidden md:block">
                      <div className="ecotour-landing-header__search-layout">
                        <BookingBar
                        variant="landing"
                        show
                        mode={searchMode}
                        experienceCategory={hydrating ? bookingCategories[0] || null : selectedExperienceCategory}
                        experienceSummary={hydrating ? bookingCategories[0]?.label || "Category" : experienceSummary}
                        locationSummary={hydrating ? "Where to?" : locationSummary}
                        dateSummary={hydrating ? formatDateRangeLabel(null, null, searchMode === "experiences") : dateSummary}
                        hasDateSelection={!hydrating && Boolean(dateRange.startDate)}
                        searchButtonLabel={hydrating && searchMode === "experiences" ? `Search ${bookingCategories[0]?.label || "experiences"}` : searchButtonLabel}
                        hasLocationSelection={!hydrating && hasLocationSelection}
                        guestMix={hydrating ? DEFAULT_GUESTS : guestMix}
                        experiencePopoverId={experiencePopoverIdLanding}
                        locationPopoverId={locationPopoverIdLanding}
                        datePopoverId={datePopoverIdLanding}
                        originInput={itineraryInputs.origin}
                        destinationInput={itineraryInputs.destination}
                        onOriginInputChange={handleItineraryOriginChange}
                        onDestinationInputChange={handleItineraryDestinationChange}
                        isExperienceOpen={isExperienceOpen}
                        isLocationOpen={isLocationOpen}
                        isDateOpen={isDateOpen}
                        experienceTriggerRef={experienceTriggerRef}
                        locationTriggerRef={locationTriggerRef}
                        dateTriggerRef={dateTriggerRef}
                        locationPopoverRef={locationPopoverRef}
                        datePopoverRef={datePopoverRef}
                        onToggleExperience={() => {
                          if (isSearchViewportCompact) {
                            openSearchWizard();
                            return;
                          }
                          setIsLocationOpen(false);
                          setIsDateOpen(false);
                          setIsExperienceOpen((p) => !p);
                        }}
                        onToggleLocation={() => {
                          if (isSearchViewportCompact) {
                            openSearchWizard();
                            return;
                          }
                          setIsDateOpen(false);
                          setIsExperienceOpen(false);
                          setIsLocationOpen((p) => !p);
                        }}
                        onToggleDate={() => {
                          if (isSearchViewportCompact) {
                            openSearchWizard();
                            return;
                          }
                          setIsLocationOpen(false);
                          setIsExperienceOpen(false);
                          setIsDateOpen((p) => !p);
                        }}
                        onClearLocation={handleLocationClear}
                        onGuestChange={handleGuestChange}
                        onSearch={isSearchViewportCompact ? openSearchWizard : handleSearch}
                        experiencePopover={showHeaderPopovers ? renderExperiencePopover(experiencePopoverIdLanding) : null}
                        locationPopover={
                          showHeaderPopovers && isLocationOpen ? (
                            <div id={locationPopoverIdLanding} ref={locationPopoverRef} className={UI.popover}>
                              <React.Suspense fallback={<DeferredControlFallback label="Loading destination search…" />}>
                                <LocationSearchCombobox
                                  index={bookingSearchIndex}
                                  context={locationContext}
                                  mode={bookingIndexMode}
                                  experienceCategory={selectedExperienceCategory?.slug || ""}
                                  experienceType={experienceType}
                                  hideLabel
                                  hideInputIcon
                                  error={bookingSearchErrorMessage}
                                  onRetry={refreshBookingSearchIndex}
                                  onResultsMeta={handleResultsMeta}
                                  helperText={
                                    searchMode === "experiences" ? `Showing ${experienceSummary.toLowerCase()} options` : ""
                                  }
                                  placeholder={
                                    searchMode === "experiences"
                                      ? `Search ${BRAND_LABELS_LOWER.destinations} or ${BRAND_LABELS_LOWER.experiences}`
                                      : `Search ${BRAND_LABELS_LOWER.districts}, nature camps or ${BRAND_LABELS_LOWER.stays}`
                                  }
                                  value={locationSelection}
                                  onSelect={handleLocationSelect}
                                  onClear={handleLocationClear}
                                  externalResults={desktopResultsWithDistricts}
                                  externalLoading={desktopLoading}
                                  externalQuery={desktopSearchEnabled ? locationQuery : ""}
                                  onQueryChange={setLocationQuery}
                                  onSwitchMode={handleModeChange}
                                  onResetSearch={handleResetSearch}
                                  remoteOnly
                                  autoFocus={isLocationOpen}
                                />
                              </React.Suspense>
                            </div>
                          ) : null
                        }
                        datePopover={
                          showHeaderPopovers && isDateOpen ? (
                            <div
                              id={datePopoverIdLanding}
                              ref={datePopoverRef}
                              className={`${UI.popoverCenter} ${searchMode === "stays" ? "!overflow-visible p-3" : ""}`}
                            >
                              <React.Suspense fallback={<DeferredControlFallback label="Loading calendar…" className="min-h-72" />}>
                                {searchMode === "stays" ? (
                                  <StaySequentialDateFields
                                    checkInDate={dateRange.startDate}
                                    checkOutDate={dateRange.endDate}
                                    onCheckInChange={(date) =>
                                      handleDateChange({ startDate: date, endDate: null })
                                    }
                                    onCheckOutChange={(date) =>
                                      handleDateChange({ startDate: dateRange.startDate, endDate: date })
                                    }
                                    minDate={new Date()}
                                    portal={false}
                                    months={2}
                                  />
                                ) : (
                                  <DateRangeInput
                                    inline
                                    value={dateRange}
                                    onChange={handleDateChange}
                                    onClose={() => setIsDateOpen(false)}
                                    single
                                    minDate={new Date()}
                                  />
                                )}
                              </React.Suspense>
                            </div>
                          ) : null
                        }
                        />
                        <ContinueBookingBar />
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* DEFAULT MODE (sticky/internal) */}
                <div
                  className={defaultNavClassName}
                  aria-hidden={showLandingNav ? "true" : undefined}
                  inert={showLandingNav ? "" : undefined}
                >
                  <div
                    className={`flex items-center justify-between gap-2 px-1.5 md:px-2 ${
                      isSticky ? "py-[2px] min-h-[44px]" : "py-0.5"
                    }`}
                  >
                    {/* Logo */}
                    <div className={`ecotour-header-brand flex min-w-0 flex-shrink items-center gap-2 sm:flex-shrink-0 ${isSticky ? "h-18" : "h-[66px]"}`}>
                      <Link to="/" className="inline-flex min-w-0 items-center">
                        <PortalBrandLogo
                          alt="EcoTour Odisha"
                          width="150"
                          height="64"
                          className="ecotour-header-brand-logo block h-full w-auto max-w-[108px] object-contain sm:max-w-none max-h-[80px]"
                          decoding="async"
                        />
                      </Link>
                      {showTestPortalBadge ? <PortalTestNavBadge /> : null}
                    </div>

                    {/* Compact booking bar */}
                    {!showLandingNav && searchMode !== "itinerary" && !isMobile ? (
                      <div className="hidden min-w-0 flex-1 justify-center xl:flex">
                        <BookingBar
                          variant="compact"
                          show
                          mode={searchMode}
                          showModeToggle
                          experienceCategory={hydrating ? bookingCategories[0] || null : selectedExperienceCategory}
                          experienceSummary={hydrating ? bookingCategories[0]?.label || "Category" : experienceSummary}
                          locationSummary={hydrating ? "Where to?" : locationSummary}
                          dateSummary={hydrating ? formatDateRangeLabel(null, null, searchMode === "experiences") : dateSummary}
                          hasDateSelection={!hydrating && Boolean(dateRange.startDate)}
                          searchButtonLabel={hydrating && searchMode === "experiences" ? `Search ${bookingCategories[0]?.label || "experiences"}` : searchButtonLabel}
                          hasLocationSelection={!hydrating && hasLocationSelection}
                          guestMix={hydrating ? DEFAULT_GUESTS : guestMix}
                          experiencePopoverId={experiencePopoverIdCompact}
                          locationPopoverId={locationPopoverIdCompact}
                          datePopoverId={datePopoverIdCompact}
                          originInput={itineraryInputs.origin}
                          destinationInput={itineraryInputs.destination}
                          onOriginInputChange={handleItineraryOriginChange}
                          onDestinationInputChange={handleItineraryDestinationChange}
                          isExperienceOpen={isExperienceOpen}
                          isLocationOpen={isLocationOpen}
                          isDateOpen={isDateOpen}
                          experienceTriggerRef={experienceTriggerRef}
                          locationTriggerRef={locationTriggerRef}
                          dateTriggerRef={dateTriggerRef}
                          locationPopoverRef={locationPopoverRef}
                          datePopoverRef={datePopoverRef}
                          onModeChange={handleModeChange}
                          onToggleExperience={() => {
                            if (isSearchViewportCompact) {
                              openSearchWizard();
                              return;
                            }
                            setIsLocationOpen(false);
                            setIsDateOpen(false);
                            setIsExperienceOpen((p) => !p);
                          }}
                          onToggleLocation={() => {
                            if (isSearchViewportCompact) {
                              openSearchWizard();
                              return;
                            }
                            setIsDateOpen(false);
                            setIsExperienceOpen(false);
                            setIsLocationOpen((p) => !p);
                          }}
                          onToggleDate={() => {
                            if (isSearchViewportCompact) {
                              openSearchWizard();
                              return;
                            }
                            setIsLocationOpen(false);
                            setIsExperienceOpen(false);
                            setIsDateOpen((p) => !p);
                          }}
                          onClearLocation={handleLocationClear}
                          onGuestChange={handleGuestChange}
                          onSearch={isSearchViewportCompact ? openSearchWizard : handleSearch}
                          experiencePopover={showHeaderPopovers ? renderExperiencePopover(experiencePopoverIdCompact) : null}
                          locationPopover={
                            showHeaderPopovers && isLocationOpen ? (
                              <div id={locationPopoverIdCompact} ref={locationPopoverRef} className={UI.popover}>
                                <React.Suspense fallback={<DeferredControlFallback label="Loading destination search…" />}>
                                  <LocationSearchCombobox
                                    index={bookingSearchIndex}
                                    context={locationContext}
                                    mode={bookingIndexMode}
                                    experienceCategory={selectedExperienceCategory?.slug || ""}
                                    experienceType={experienceType}
                                    hideLabel
                                    hideInputIcon
                                    error={bookingSearchErrorMessage}
                                    onRetry={refreshBookingSearchIndex}
                                    onResultsMeta={handleResultsMeta}
                                    helperText={
                                      searchMode === "experiences" ? `Showing ${experienceSummary.toLowerCase()} options` : ""
                                    }
                                    placeholder={
                                      searchMode === "experiences"
                                        ? `Search ${BRAND_LABELS_LOWER.destinations} or ${BRAND_LABELS_LOWER.experiences}`
                                        : `Search ${BRAND_LABELS_LOWER.districts}, nature camps or ${BRAND_LABELS_LOWER.stays}`
                                    }
                                    value={locationSelection}
                                    onSelect={handleLocationSelect}
                                    onClear={handleLocationClear}
                                    externalResults={desktopResultsWithDistricts}
                                    externalLoading={desktopLoading}
                                    externalQuery={desktopSearchEnabled ? locationQuery : ""}
                                    onQueryChange={setLocationQuery}
                                    onSwitchMode={handleModeChange}
                                    onResetSearch={handleResetSearch}
                                    remoteOnly
                                    autoFocus={isLocationOpen}
                                  />
                                </React.Suspense>
                              </div>
                            ) : null
                          }
                          datePopover={
                            showHeaderPopovers && isDateOpen ? (
                              <div
                                id={datePopoverIdCompact}
                                ref={datePopoverRef}
                                className={`${UI.popoverCenter} ${searchMode === "stays" ? "!overflow-visible p-3" : ""}`}
                              >
                                <React.Suspense fallback={<DeferredControlFallback label="Loading calendar…" className="min-h-72" />}>
                                  {searchMode === "stays" ? (
                                    <StaySequentialDateFields
                                      checkInDate={dateRange.startDate}
                                      checkOutDate={dateRange.endDate}
                                      onCheckInChange={(date) =>
                                        handleDateChange({ startDate: date, endDate: null })
                                      }
                                      onCheckOutChange={(date) =>
                                        handleDateChange({ startDate: dateRange.startDate, endDate: date })
                                      }
                                      minDate={new Date()}
                                      portal={false}
                                      months={2}
                                    />
                                  ) : (
                                    <DateRangeInput
                                      inline
                                      value={dateRange}
                                      onChange={handleDateChange}
                                      onClose={() => setIsDateOpen(false)}
                                      single
                                      minDate={new Date()}
                                    />
                                  )}
                                </React.Suspense>
                              </div>
                            ) : null
                          }
                        />
                      </div>
                    ) : (
                      <div className="flex-1" />
                    )}

                    {/* Right actions */}
                    <div className="ecotour-header-actions ml-auto flex min-w-0 flex-shrink-0 items-center gap-1.5">
                      <RewardCoinPill className="ecotour-header-reward-control" />
                      <CartSummaryPill
                        onClick={() => setIsCartConfiguratorOpen(true)}
                        className="ecotour-header-cart-control h-9"
                      />

                      <ThemeToggle />

                      <button
                        ref={menuButtonRef}
                        type="button"
                        className={`${UI.pill} px-0 py-0`}
                        onClick={() => setIsMenuOpen((prev) => !prev)}
                        aria-expanded={isMenuOpen}
                        aria-haspopup="true"
                        aria-controls="global-navigation-menu"
                        aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                      >
                        <span className={UI.iconPillBlue}>
                          <i className="fa-solid fa-layer-group" aria-hidden="true" />
                        </span>
                        <span className="hidden xs:inline pr-2 text-[12px]">Menu</span>
                      </button>

                      {(isSearchViewportCompact || hydrating) ? (
                        <button
                          type="button"
                          className={`${UI.iconBtn} min-[1100px]:hidden`}
                          onClick={handleHeaderSearchIconClick}
                          aria-label="Open search"
                        >
                          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                        </button>
                      ) : null}

                      <button
                        type="button"
                        className={`hidden lg:inline-flex ${UI.iconBtn}`}
                        onClick={() => setIsSideMenuOpen(true)}
                        aria-label="Open quick drawer"
                      >
                        <i className="fa-solid fa-user" aria-hidden="true" />
                      </button>

                      <button
                        type="button"
                        className={`lg:hidden ${UI.iconBtn}`}
                        onClick={() => setIsSideMenuOpen(true)}
                        aria-label="Open navigation"
                      >
                        <i className="fa-solid fa-user" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mega menu dropdown */}
                {renderMenuDropdown()}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Menus + drawers */}
      {isMobileMenuOpen ? (
        <React.Suspense fallback={<span className="sr-only" role="status">Loading navigation…</span>}>
          <MobileMenu isOpen onClose={() => setIsMobileMenuOpen(false)} />
        </React.Suspense>
      ) : null}
      {isSideMenuOpen || pendingAuthRequest ? (
        <React.Suspense fallback={<span className="sr-only" role="status">Loading account menu…</span>}>
          <SideMenu
            isOpen={isSideMenuOpen}
            authRequest={pendingAuthRequest}
            onAuthRequestHandled={handleAuthRequestHandled}
            onClose={handleSideMenuClose}
          />
        </React.Suspense>
      ) : null}
      {isCartConfiguratorOpen ? (
        <React.Suspense
          fallback={(
            <div className="sr-only" role="status" aria-live="polite">
              Loading trip summary…
            </div>
          )}
        >
          <ExperienceCartConfigurator
            open
            initialStep={cartInitialStep}
            initialCartItemKey={cartResumeTarget?.cartItemKey || ""}
            initialCartItemIdentifier={cartResumeTarget?.identifier || ""}
            initialCartItem={cartResumeTarget?.resumeItem || null}
            mode="cart"
            identifier=""
            experienceType="safari"
            title="Trip summary"
            destinationTitle="Trip summary"
            districtName=""
            onEditItem={handleEditItem}
            onClose={() => {
              setIsCartConfiguratorOpen(false);
              setCartInitialStep(1);
              clearContinueBookingResumeIntent();
            }}
          />
        </React.Suspense>
      ) : null}
      {isSearchViewportCompact && isSearchWizardOpen ? (
        <div
          className={`ecotour-search-wizard fixed inset-0 z-[1300] ${isSearchWizardOpen ? "pointer-events-auto" : "pointer-events-none"}`}
          aria-hidden={!isSearchWizardOpen}
          inert={isSearchWizardOpen ? undefined : ""}
        >
          <div
            className={`ecotour-search-wizard__backdrop absolute inset-0 transition-opacity duration-300 ${
              isSearchWizardOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => {
              setIsSearchWizardOpen(false);
              setMobileSheet("main");
            }}
          />
            <div
              className={`absolute inset-x-0 bottom-0 transform transition-transform duration-300 ${
                isSearchWizardOpen ? "translate-y-0" : "translate-y-full"
              }`}
              onClick={(event) => event.stopPropagation()}
            >
            <div
              className="ecotour-search-wizard__sheet mx-auto flex w-full max-w-2xl h-[95svh] flex-col rounded-t-3xl bg-white px-4 pb-[calc(env(safe-area-inset-bottom)+10px)] pt-2 shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby={wizardTitleId}
              aria-describedby={wizardDescId}
              ref={wizardRef}
              tabIndex={-1}
            >
              <div className="mx-auto h-1.5 w-12 rounded-full bg-slate-200" />
              {mobileSheet === "main" ? (
                <div className="mt-2 flex items-center justify-between">
                  <div>
                    <p id={wizardDescId} className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                      Quick search
                    </p>
                    <h2 id={wizardTitleId} className="text-base font-semibold text-slate-900">{sheetTitle}</h2>
                  </div>
                  <button
                    type="button"
                    className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600"
                    onClick={() => {
                      setIsSearchWizardOpen(false);
                      setMobileSheet("main");
                    }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex items-center justify-between">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600"
                    onClick={() => setMobileSheet("main")}
                  >
                    <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                    Back
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600"
                    onClick={() => {
                      setIsSearchWizardOpen(false);
                      setMobileSheet("main");
                    }}
                  >
                    Close
                  </button>
                </div>
              )}

              <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1 pb-[calc(env(safe-area-inset-bottom)+96px)]">
                {mobileSheet === "main" ? (
                  <div className="space-y-4">
                    <div className="rounded-full border border-slate-200 bg-white p-1">
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          { key: "stays", label: "Stays", icon: "fa-solid fa-bed" },
                          { key: "experiences", label: "Experiences", icon: "fa-solid fa-compass" },
                        ].map((item) => {
                          const isActive = searchMode === item.key;
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => handleModeChange(item.key)}
                              className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition ${
                                isActive
                                  ? "bg-[#176b45] text-white"
                                  : "text-slate-600 hover:text-slate-900"
                              }`}
                            >
                              <i className={`${item.icon} text-[12px]`} aria-hidden="true" />
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {isExperienceMode ? (
                      <button
                        type="button"
                        onClick={() => setMobileSheet("category")}
                        className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left"
                      >
                        <span>
                          <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Category</span>
                          <span className="mt-1 block text-sm font-semibold text-slate-900">{experienceSummary}</span>
                        </span>
                        <i className="fa-solid fa-chevron-right text-slate-400" aria-hidden="true" />
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => setMobileSheet("location")}
                      className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left"
                    >
                      <span>
                        <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Where</span>
                        <span className="mt-1 block text-sm font-semibold text-slate-900">{locationSummary}</span>
                      </span>
                      <i className="fa-solid fa-chevron-right text-slate-400" aria-hidden="true" />
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setMobileDraftDates(coerceDateRange(dateRange));
                        setMobileSheet("date");
                      }}
                      className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left"
                    >
                      <span>
                        <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">When</span>
                        <span className="mt-1 block text-sm font-semibold text-slate-900">{dateSummary}</span>
                      </span>
                      <i className="fa-solid fa-chevron-right text-slate-400" aria-hidden="true" />
                    </button>

                    {showGuestMix ? (
                      <button
                        type="button"
                        onClick={() => setMobileSheet("guests")}
                        className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left"
                      >
                        <span>
                          <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Guests</span>
                          <span className="mt-1 block text-sm font-semibold text-slate-900">{guestSummary}</span>
                        </span>
                        <i className="fa-solid fa-chevron-right text-slate-400" aria-hidden="true" />
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={handleResetSearch}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-semibold text-slate-600"
                    >
                      Reset selection
                    </button>
                  </div>
                ) : null}

                {mobileSheet === "category" ? (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-slate-900">Choose a category</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {bookingCategories.map((entry) => {
                        const key = entry.slug || entry.key;
                        const isActive = key === selectedExperienceCategory?.slug;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              handleExperienceCategoryChange(entry);
                              setMobileSheet("main");
                            }}
                            aria-pressed={isActive}
                            className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                              isActive
                                ? "border-[#176b45] bg-[#176b45] text-white"
                                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                            }`}
                          >
                            <span
                              className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${
                                isActive ? "bg-white/15 text-white" : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              <i className={entry.icon} aria-hidden="true" />
                            </span>
                            <span>{entry.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {mobileSheet === "location" ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {searchMode === "experiences" ? "Where do you want to go?" : "Where do you want to stay?"}
                      </p>
                      <div className="mt-3">
                        <React.Suspense fallback={<DeferredControlFallback label="Loading destination search…" className="min-h-72" />}>
                          <MobileWhereSheet
                            mode={bookingIndexMode}
                            experienceType={experienceType}
                            query={wizardLocationQuery}
                            onQueryChange={setWizardLocationQuery}
                            loading={wizardLoading}
                            results={wizardResultsToRender}
                            onSelectItem={handleMobileLocationPick}
                            onSwitchMode={handleModeChange}
                            onResetSearch={handleResetSearch}
                            onResultsMeta={handleResultsMeta}
                          />
                        </React.Suspense>
                      </div>
                    </div>
                  </div>
                ) : null}

                {mobileSheet === "date" ? (
                  <div className="flex min-h-[60svh] flex-col rounded-2xl border border-slate-200 bg-white p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">When</p>
                    <div className="mt-2">
                      {searchMode === "stays" ? (
                        <React.Suspense fallback={<DeferredControlFallback label="Loading calendar…" />}>
                          <StaySequentialDateFields
                            checkInDate={mobileDraftDates?.startDate || null}
                            checkOutDate={mobileDraftDates?.endDate || null}
                            onCheckInChange={(date) =>
                              setMobileDraftDates((prev) => ({
                                startDate: date,
                                endDate: prev?.endDate || null,
                              }))
                            }
                            onCheckOutChange={(date) =>
                              setMobileDraftDates((prev) => ({
                                startDate: prev?.startDate || null,
                                endDate: date,
                              }))
                            }
                            minDate={new Date()}
                            portal
                            months={1}
                            compact
                          />
                        </React.Suspense>
                      ) : (
                        <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-500">
                          Date
                          <input
                            type="date"
                            value={
                              mobileDraftDates?.startDate
                                ? format(mobileDraftDates.startDate, "yyyy-MM-dd")
                                : ""
                            }
                            onChange={(event) => {
                              const value = event.target.value;
                              const nextDate = value ? parseISO(value) : null;
                              setMobileDraftDates({
                                startDate: isValid(nextDate) ? nextDate : null,
                                endDate: null,
                              });
                            }}
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900"
                          />
                        </label>
                      )}
                    </div>
                    <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">
                      {formatDateRangeTopLabel(
                        mobileDraftDates?.startDate || null,
                        mobileDraftDates?.endDate || null,
                        searchMode === "experiences",
                      )}
                    </div>
                    {searchMode === "stays" &&
                    isValid(mobileDraftDates?.startDate) &&
                    isValid(mobileDraftDates?.endDate) ? (
                      <p className="mt-2 text-xs font-semibold text-slate-500">
                        {Math.max(
                          1,
                          differenceInCalendarDays(
                            mobileDraftDates.endDate,
                            mobileDraftDates.startDate,
                          ),
                        )}{" "}
                        night
                        {differenceInCalendarDays(
                          mobileDraftDates.endDate,
                          mobileDraftDates.startDate,
                        ) > 1
                          ? "s"
                          : ""}
                      </p>
                    ) : null}
                    <div className="mt-3 flex items-center justify-between">
                      <button
                        type="button"
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"
                        onClick={() => {
                          setMobileDraftDates(coerceDateRange(dateRange));
                        }}
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        className="ecotour-search-wizard__primary rounded-full px-4 py-2 text-sm font-semibold"
                        onClick={() => {
                          handleDateChange(mobileDraftDates || dateRange);
                          setMobileSheet("main");
                        }}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : null}

                {mobileSheet === "guests" ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Guests</p>
                    <div className="mt-2">
                      <GuestMixSelector value={guestMix} onChange={handleGuestChange} maxGuests={16} expanded={isMobile} />
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        className="ecotour-search-wizard__primary rounded-full px-4 py-2 text-sm font-semibold"
                        onClick={() => setMobileSheet("main")}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="mt-3 flex shrink-0 items-center gap-2 border-t border-slate-100 bg-white pt-3">
                <button
                  type="button"
                  className="ecotour-search-wizard__primary min-h-11 flex-1 rounded-full px-4 py-2 text-sm font-semibold transition"
                  onClick={handleSearchFromWizard}
                >
                  {searchButtonLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default HeaderThree;
